import apiUrl from './api';
import { postWithAuth, getWithAuth } from '@/lib/httpAdapter';
import { formatDateToLocalYYYYMMDD } from '@/lib/formatLocalDate';
import type { SkillRecommendation } from './skillRecommendations';
import {
  HARNESS_UNAVAILABLE,
  harnessLedgerSchema,
  type HarnessLedger,
  sessionExplorerMapBatchSchema,
  sessionExplorerSessionsSchema,
  sessionExplorerStatusSchema,
  sessionMapSchema,
  type SessionExplorerMapBatch,
  type SessionExplorerSessionsPage,
  type SessionExplorerStatus,
  type SessionMap,
} from './sessionExplorerSchema';
import { toast } from 'sonner';

// Track if computation toast has been shown to avoid duplicates
let computationToastShown = false;
let computationToastTimestamp = 0;
const COMPUTATION_TOAST_COOLDOWN = 5000; // 5 seconds cooldown

// Helper function to show computation toast (with deduplication)
function showComputationToastIfNeeded() {
  const now = Date.now();
  // Only show if not shown recently (within cooldown period)
  if (
    !computationToastShown ||
    now - computationToastTimestamp > COMPUTATION_TOAST_COOLDOWN
  ) {
    computationToastShown = true;
    computationToastTimestamp = now;
    toast.info(
      'Sorry for the wait, we are aggregating data for your next visit!'
    );
  }
}

const endpoint = `${apiUrl}overallTeamInsightOverview/`;
const cursorEndpoint = `${apiUrl}aiTeamInsightOverview/`;
const teamInsightPerMemberEndpoint = `${apiUrl}teamInsightPerMember/`;

// New split cursor endpoints
const cursorActivityEndpoint = `${apiUrl}aiTeamInsightActivity/`;
// Updated to use daily aggregation table endpoint for better performance
const cursorMetricsEndpoint = `${apiUrl}teamInsights/dailyAIAnalytics/`;
// Fallback to old endpoint if daily table not available
const cursorMetricsEndpointFallback = `${apiUrl}aiTeamInsightMetrics/`;
const cursorOutputEndpoint = `${apiUrl}aiTeamInsightOutput/`;

// New optimized team insight endpoints
// Updated to use daily snapshots endpoint for better performance
const teamInsightCoreAnalyticsEndpoint = `${apiUrl}teamInsights/dailyCoreAnalytics/`;
// Live core analytics, computed from PRReviewStatsTable rather than the
// pre-computed daily table. Required for seat-filtered reads: the daily
// endpoint stores per-team averages with no per-person rows and refuses them.
const teamInsightCoreAnalyticsEndpointLive = `${apiUrl}teamInsightCoreAnalytics/`;
const teamInsightCoreAnalyticsTeamsEndpoint = `${apiUrl}teamInsights/dailyCoreAnalytics/`;
// Fallback to old endpoints if daily table not available
const teamInsightCoreAnalyticsTeamsEndpointFallback = `${apiUrl}teamInsightCoreAnalyticsTeams/`;
// Removed - these are now included in dailyCoreAnalytics endpoint
// const teamInsightCoreAnalyticsTeamChartEndpoint = `${apiUrl}teamInsightCoreAnalyticsTeamChart/`;
// const teamInsightCoreAnalyticsEngineerChartEndpoint = `${apiUrl}teamInsightCoreAnalyticsEngineerChart/`;
const teamInsightCodeReviewAnalyticsEndpoint = `${apiUrl}teamInsightCodeReviewAnalytics/`;

// Compute endpoints for triggering daily analytics computation
const computeDailyCoreAnalyticsEndpoint = `${apiUrl}teamInsights/computeDailyCoreAnalytics/`;
const computeDailyAIAnalyticsEndpoint = `${apiUrl}teamInsights/computeDailyAIAnalytics/`;
const computeDailyCodeReviewAnalyticsEndpoint = `${apiUrl}teamInsights/computeDailyCodeReviewAnalytics/`;
const computeDailyExternalAnalyticsEndpoint = `${apiUrl}teamInsights/computeDailyExternalAnalytics/`;
// Split code review analytics endpoints (parallel loading)
// Updated to use daily aggregation table endpoint for better performance
const teamInsightCodeReviewAnalyticsMetricsEndpoint = `${apiUrl}teamInsights/dailyCodeReviewAnalytics/`;
// Fallback to old endpoint if daily table not available
const teamInsightCodeReviewAnalyticsMetricsEndpointFallback = `${apiUrl}teamInsightCodeReviewAnalyticsMetrics/`;
const teamInsightCodeReviewAnalyticsQualityChartEndpoint = `${apiUrl}teamInsightCodeReviewAnalyticsQualityChart/`;
const teamInsightCodeReviewAnalyticsTurnaroundChartEndpoint = `${apiUrl}teamInsightCodeReviewAnalyticsTurnaroundChart/`;
const teamInsightCodeReviewAnalyticsOutputTypeChartEndpoint = `${apiUrl}teamInsightCodeReviewAnalyticsOutputTypeChart/`;
const teamInsightCodeReviewAnalyticsDepthChartEndpoint = `${apiUrl}teamInsightCodeReviewAnalyticsDepthChart/`;
const teamInsightExternalAnalyticsEndpoint = `${apiUrl}teamInsightExternalAnalytics/`;

// Split external analytics endpoints (parallel loading)
// Updated to use daily aggregation table endpoint for better performance
const teamInsightExternalAnalyticsMetricsEndpoint = `${apiUrl}teamInsights/dailyExternalAnalytics/`;
// Fallback to old endpoint if daily table not available
const teamInsightExternalAnalyticsMetricsEndpointFallback = `${apiUrl}teamInsightExternalAnalyticsMetrics/`;
const teamInsightExternalAnalyticsRevertedPRsEndpoint = `${apiUrl}teamInsightExternalAnalyticsRevertedPRs/`;
const teamInsightExternalAnalyticsCodeTurnoverEndpoint = `${apiUrl}teamInsightExternalAnalyticsCodeTurnover/`;
const teamInsightExternalAnalyticsBugReportsEndpoint = `${apiUrl}teamInsightExternalAnalyticsBugReports/`;

// Optimized metrics summary endpoint (all 7 metrics in one call)
const teamInsightMetricsSummaryEndpoint = `${apiUrl}teamInsightMetricsSummary/`;

// Cost charts endpoint
const costChartsEndpoint = `${apiUrl}costCharts/`;

// Work Type Per Person endpoint
const workTypePerPersonEndpoint = `${apiUrl}teamInsightWorkTypePerPerson/`;

export interface TeamInsightOverviewRequest {
  org_uuid?: string;
  repo_name?: string;
  team_uuids?: string[];
  date_range: string;
  date_interval?: {
    start_date: string;
    end_date: string;
  };
  integration?: 'claude' | 'cursor' | 'codex' | 'all';
  /**
   * Team Insights' "Paid seats only" toggle. When true the backend narrows the
   * read to the org's paid-seat holders and, for the team breakdown, collapses
   * the org's real teams into one synthetic "paid_seats" group.
   *
   * Only honoured by the LIVE endpoints. The pre-computed daily analytics
   * tables store per-team averages with no per-person rows, so they reject a
   * seat-filtered request outright - callers must skip the daily attempt when
   * this is set (see `shouldSkipDailyEndpoint`).
   */
  seats_only?: boolean;
}

// TeamInsightPerMember interfaces
export interface TeamInsightPerMemberRequest {
  org_uuid?: string;
  team_uuids?: string[];
  repo_name?: string;
  date_range: string;
  date_interval?: {
    start_date: string;
    end_date: string;
  };
  /**
   * Team Insights' "Paid seats only" toggle. When true the backend narrows the
   * read to the org's paid-seat holders and, for the team breakdown, collapses
   * the org's real teams into one synthetic "paid_seats" group.
   *
   * Only honoured by the LIVE endpoints. The pre-computed daily analytics
   * tables store per-team averages with no per-person rows, so they reject a
   * seat-filtered request outright - callers must skip the daily attempt when
   * this is set (see `shouldSkipDailyEndpoint`).
   */
  seats_only?: boolean;
}

export interface TeamInsightMemberMetrics {
  user_id: string;
  user_uuid: string;
  name: string;
  email?: string | null;
  team_name: string;
  team_uuid: string;
  image?: string | null;
  merge_frequency?: number | null;
  loc_by_ai?: number | null;
  /** Cursor LoC (lines by AI from Cursor). When present, used for Cursor column and Total LoC. */
  cursor_loc?: number | null;
  /** Claude LoC (lines by AI from Claude). When present, used for Claude column and Total LoC. */
  claude_loc?: number | null;
  /** Codex LoC (lines by AI from Codex). When present, used for Codex column and Total LoC. */
  codex_loc?: number | null;
  /** Total LoC by AI (cursor + claude + codex or backend-computed). When present, used for Total LoC by AI column. */
  total_loc_by_ai?: number | null;
  ai_code_percentage?: number | null;
  acceptance_rate?: number | null;
  total_prs_merged?: number | null;
  total_prs_created?: number | null;
  total_lines_added?: number | null;
  total_lines_deleted?: number | null;
  /** Sum of engineer output scores across merged PRs in the selected period */
  total_pr_points?: number | null;
  /** Merged PRs with bug-related output classification (bugs_score > 0) in the period */
  bug_fix_prs_merged?: number | null;
  status: string;
  trend?: string | null;
}

export interface TeamInsightPerMemberTeamData {
  team_uuid: string;
  team_name: string;
  total_members: number;
  members_with_cursor_data: number;
  average_merge_frequency?: number | null;
  average_ai_code_percentage?: number | null;
  average_acceptance_rate?: number | null;
}

export interface TeamInsightPerMemberResponse {
  org_uuid?: string;
  team_data: TeamInsightPerMemberTeamData[];
  members: TeamInsightMemberMetrics[];
  unmapped_members?: TeamInsightMemberMetrics[];
  total_members: number;
  total_teams: number;
  members_with_cursor_data: number;
  date_range: string;
  error?: string | null;
}

export interface EngineerInsight {
  engineer_email: string;
  engineer_name: string;
  average_score: number;
  total_prs: number;
  previous_period_average: number;
  score_change: number;
  trend_strength: string; // Changed from enum to string for flexibility
  comparison_text?: string; // e.g., "Up from 3.2" or "Down from 5.1"
}

export interface TeamInsight {
  team_uuid: string;
  team_name: string;
  engineer_insights: EngineerInsight[];
  team_average: number;
  total_engineers: number;
  avg_review_quality: number;
  comparison_text?: string; // e.g., "Up from 3.2" or "Down from 5.1"
}

export interface TeamInsightOverviewResponse {
  org_uuid?: string;
  team_insights: TeamInsight[];
  overall_team_performance_average: number;
  overall_team_performance_trend: number;
  overall_avg_review_quality: number;
  overall_avg_review_quality_trend: number;
  overall_median_review_turnaround: number;
  overall_median_review_turnaround_trend: number;
  ai_adoption: number;
  ai_adoption_trend: number;
  overall_innovation_ratio: number;
  overall_innovation_ratio_trend: number;
  overall_bug_ratio: number;
  overall_bug_ratio_trend: number;
  overall_ai_savings: number;
  overall_ai_savings_trend: number;
  output_per_team_chart?: {
    [teamName: string]: {
      data: object;
      upper_quantile: number;
      lower_quantile: number;
      output_data: {
        [date: string]: {
          score: number;
          prs: Array<{
            pr_number: string;
            score: number;
          }>;
        };
      };
    };
  };
  output_per_engineer_chart?: {
    [engineerName: string]: {
      data: object;
      upper_quantile: number;
      lower_quantile: number;
      output_data: {
        [date: string]: {
          score: number;
          prs: Array<{
            pr_number: string;
            score: number;
          }>;
        };
      };
    };
  };
  code_review_output_type_chart?: {
    [date: string]: {
      bugs: number;
      ktlo: number;
      features: number;
      innovation: number;
    };
  };
  total_engineers: number;
  total_teams: number;
  date_range: string;
  // Reverted PRs data (same structure as revertedPRsWeekly but filtered by teams)
  reverted_prs_current_week: {
    count: number;
    week: string;
    week_start?: string;
    week_end?: string;
  };
  reverted_prs_weekly_data: Array<{
    week: string;
    count: number;
    week_start: string;
    week_end: string;
  }>;
  reverted_prs_average_per_week: number;
  reverted_prs_total_reverts: number;
  reverted_prs_total_prs_period: number;
  // Code Turnover data (3-month window)
  code_turnover_weekly_data: Array<{
    week: string;
    turnoverPct: number;
    totalLinesChanged: number;
  }>;
  code_turnover_average_pct: number;
  code_turnover_median_pct: number;
  code_turnover_top10_pct: number;
  code_turnover_total_lines_changed: number;
  bug_reports_current_week: {
    created: number;
    resolved: number;
    week_start: string;
    bug_tickets?: Array<{
      ticket_id: string;
      ticket_number: string;
      title: string;
      url: string;
      status: string;
      project_name: string;
      created_at: string;
      author: string;
      bugs_score: number;
    }>;
  };
  bug_reports_weekly_data: Array<{
    week_start: string;
    created: number;
    resolved: number;
    bug_tickets?: Array<{
      ticket_id: string;
      ticket_number: string;
      title: string;
      url: string;
      status: string;
      project_name: string;
      created_at: string;
      author: string;
      bugs_score: number;
    }>;
  }>;
  bug_reports_total_created: number;
  bug_reports_total_resolved: number;
  bug_reports_open_count: number;
  bug_reports_average_created_per_week: number;
  bug_reports_average_resolved_per_week: number;
  bug_reports_project_breakdown: Array<{
    project_name: string;
    bug_count: number;
    percentage: number;
    bug_tickets?: Array<{
      ticket_id: string;
      ticket_number: string;
      title: string;
      url: string;
      status: string;
      project_name: string;
      created_at: string;
      author: string;
      bugs_score: number;
    }>;
  }>;
  bug_reports_error?: string | null;
  error: string | null;
}

// New types for Cursor Team Insights Overview
export interface AIMemberMetrics {
  user_id: string;
  name: string;
  email: string | null;
  role: string;
  ai_contribution_percentage: number;
  merge_frequency: number;
  total_lines_added: number;
  ai_lines_added: number;
  total_lines_deleted: number;
  ai_lines_deleted: number;
  total_applies: number;
  total_accepts: number;
  total_rejects: number;
  acceptance_rate: number;
  productivity_score: number;
  status: string;
  trend: string;
  user_uuid: string | null;
  team_uuid: string | null;
  total_impact_score: number | null;
  prs_merged: number | null;
  image: string | null;
}

export interface CursorTeamInsightOverviewResponse {
  org_uuid: string;
  teams_with_cursor_data: string[];
  teams_without_cursor_data: string[];
  ai_adoption: number;
  ai_adoption_comparison_text?: string | null;
  overall_ai_savings: number;
  ai_savings_comparison_text?: string | null;
  ai_savings_breakdown?: {
    total_engineers: number;
    business_days: number;
    total_work_hours: number;
    ai_usage_percentage: number;
    ai_assisted_hours: number;
    hourly_rate: number;
  };
  ai_code_activity_chart: Array<{
    date: string;
    additions: number;
    deletions: number;
    aiAdditions: number;
    aiDeletions: number;
  }>;
  ai_code_percentage_chart: Array<{
    week: string;
    aiCodePct: number;
    medianPct: number;
    top10Pct: number;
  }>;
  code_output_chart: Array<{
    week: string;
    mergedUnits: number;
    closedUnits: number;
    openUnits: number;
    totalUnits: number;
    medianUnits: number;
    top10Units: number;
  }>;
  ai_usage_per_member: AIMemberMetrics[];
  total_teams_with_cursor: number;
  total_teams_without_cursor: number;
  date_range: string;
  error: string | null;
}

// New split cursor endpoint response types
export interface CursorTeamInsightActivityResponse {
  org_uuid?: string;
  teams_with_cursor_data: string[];
  teams_without_cursor_data: string[];
  ai_code_activity_chart: Array<{
    date: string;
    additions: number;
    deletions: number;
    aiAdditions: number;
    aiDeletions: number;
  }>;
  ai_code_percentage_chart: Array<{
    week: string;
    aiCodePct: number;
    medianPct: number;
    top10Pct: number;
  }>;
  total_teams_with_cursor: number;
  total_teams_without_cursor: number;
  date_range: string;
  error?: string | null;
}

export interface CursorTeamInsightMetricsResponse {
  org_uuid?: string;
  teams_with_cursor_data: string[];
  teams_without_cursor_data: string[];
  ai_adoption: number;
  overall_ai_savings: number;
  ai_adoption_comparison_text?: string | null;
  ai_savings_comparison_text?: string | null;
  ai_adoption_trend?: number;
  ai_savings_trend?: number;
  ai_savings_breakdown?: {
    total_engineers: number;
    business_days: number;
    total_work_hours: number;
    ai_usage_percentage: number;
    ai_assisted_hours: number;
    hourly_rate: number;
  };
  ai_usage_per_member: AIMemberMetrics[];
  total_teams_with_cursor: number;
  total_teams_without_cursor: number;
  date_range: string;
  team_summaries?: { [teamUuid: string]: string };
  // Analytics-compatible fields
  ai_code_contribution?: {
    overall_percentage: number;
    total_lines_added: number;
    ai_lines_added: number;
    total_lines_deleted: number;
    ai_lines_deleted: number;
    comparison_text?: string; // e.g., "Up from 45.2%" or "Down from 52.3%"
  };
  top_contributors?: Array<{
    name: string;
    email: string;
    ai_contribution_percentage: number;
    lines_added: number;
    rank: number;
    image: string;
  }>;
  insights?: Array<{
    insight_type: string;
    message: string;
    severity: string;
    actionable: boolean;
    action?: string;
    affected_users?: string[];
    top_performer?: string;
  }>;
  trends?: {
    overall_growth: number;
    ai_adoption_growth: number;
    productivity_growth: number;
    peak_month: string;
    peak_ai_percentage: number;
  };
  most_used_model?: string;
  error?: string | null;
  // Computation status for frontend UX (indicates if data was computed on-the-fly)
  computation_status?: ComputationStatus;
  /** Per-day AI data from daily AI analytics (accepts and ai_lines by date, cursor vs claude) */
  daily_data?: Array<{
    date: string;
    cursor_accepts: number;
    claude_accepts: number;
    total_accepts: number;
    cursor_ai_lines?: number;
    claude_ai_lines?: number;
    /** Total lines added in merged PRs that day (from daily_core) */
    total_lines_merged?: number;
  }>;
}

export interface CursorTeamInsightOutputResponse {
  org_uuid?: string;
  teams_with_cursor_data: string[];
  teams_without_cursor_data: string[];
  code_output_chart: Array<{
    week: string;
    mergedUnits: number;
    closedUnits: number;
    openUnits: number;
    totalUnits: number;
    medianUnits: number;
    top10Units: number;
  }>;
  total_teams_with_cursor: number;
  total_teams_without_cursor: number;
  date_range: string;
  error?: string | null;
}

// New optimized endpoint response interfaces
export interface TeamInsightCoreAnalyticsResponse {
  org_uuid?: string;
  team_insights: TeamInsight[];
  overall_team_performance_average: number;
  output_per_team_chart?: {
    [teamName: string]: {
      data: object;
      upper_quantile: number;
      lower_quantile: number;
      output_data: {
        [date: string]: {
          score: number;
          prs: Array<{
            pr_number: string;
            score: number;
          }>;
        };
      };
    };
  };
  output_per_engineer_chart?: {
    [engineerName: string]: {
      data: object;
      upper_quantile: number;
      lower_quantile: number;
      output_data: {
        [date: string]: {
          score: number;
          prs: Array<{
            pr_number: string;
            score: number;
          }>;
        };
      };
    };
  };
  total_engineers: number;
  total_teams: number;
  date_range: string;
  error?: string | null;
  overall_comparison_text?: string; // e.g., "Up from 3.2" or "Down from 5.1"
}

// Split core analytics response interfaces
export interface TeamInsightCoreAnalyticsTeamsResponse {
  org_uuid?: string;
  team_insights: TeamInsight[];
  overall_team_performance_average: number;
  overall_comparison_text?: string;
  total_engineers: number;
  total_teams: number;
  date_range: string;
  error?: string | null;
  // Chart data from daily endpoint (available but not always used)
  output_per_team_chart?: {
    [key: string]: any;
  };
  output_per_engineer_chart?: {
    [key: string]: any;
  };
  // Computation status for frontend UX (indicates if data was computed on-the-fly)
  computation_status?: ComputationStatus;
}

export interface TeamInsightCoreAnalyticsTeamChartResponse {
  org_uuid?: string;
  output_per_team_chart?: {
    [teamName: string]: {
      data: object;
      upper_quantile: number;
      lower_quantile: number;
      output_data: {
        [date: string]: {
          score: number;
          prs: Array<{
            pr_number: string;
            score: number;
          }>;
        };
      };
    };
  };
  date_range: string;
  error?: string | null;
}

export interface TeamInsightCoreAnalyticsEngineerChartResponse {
  org_uuid?: string;
  output_per_engineer_chart?: {
    [engineerName: string]: {
      data: object;
      upper_quantile: number;
      lower_quantile: number;
      output_data: {
        [date: string]: {
          score: number;
          prs: Array<{
            pr_number: string;
            score: number;
          }>;
        };
      };
    };
  };
  date_range: string;
  error?: string | null;
}

export interface TeamInsightCodeReviewAnalyticsResponse {
  org_uuid?: string;
  overall_avg_review_quality: number;
  /** Percent of PRs in the period that had any review. Null when unmeasured. */
  review_coverage?: number | null;
  /** Numerator behind review_coverage. Null when unmeasured. */
  reviewed_prs?: number | null;
  /** Denominator behind review_coverage. */
  total_prs?: number | null;
  overall_median_review_turnaround: number;
  code_review_output_type_chart?: {
    [key: string]: any;
  };
  code_review_quality_chart?: {
    [key: string]: any;
  };
  code_review_turnaround_chart?: {
    [key: string]: any;
  };
  code_review_depth_chart?: {
    [key: string]: any;
  };
  date_range: string;
  error?: string | null;
  quality_comparison_text?: string; // e.g., "Up from 8.2" or "Down from 9.1"
  turnaround_comparison_text?: string; // e.g., "Up from 4.2h" or "Down from 6.8h"
}

// Computation status interface for daily aggregation endpoints
export interface ComputationStatus {
  needs_computation: boolean;
  missing_dates: string[]; // YYYY-MM-DD format
  tables_needing_computation: string[]; // e.g., ["core", "code_review", "external", "ai"]
  estimated_days: number;
}

// Split code review analytics response interfaces (parallel loading)
export interface TeamInsightCodeReviewAnalyticsMetricsResponse {
  org_uuid?: string;
  overall_avg_review_quality: number;
  /** Percent of PRs in the period that had any review. Null when unmeasured. */
  review_coverage?: number | null;
  /** Numerator behind review_coverage. Null when unmeasured. */
  reviewed_prs?: number | null;
  /** Denominator behind review_coverage. */
  total_prs?: number | null;
  overall_median_review_turnaround: number;
  quality_comparison_text?: string | null;
  turnaround_comparison_text?: string | null;
  overall_avg_review_quality_trend?: number;
  overall_median_review_turnaround_trend?: number;
  date_range: string;
  error?: string | null;
  // Chart data from daily endpoint (available but not always used)
  code_review_quality_chart?: {
    [key: string]: any;
  };
  code_review_turnaround_chart?: {
    [key: string]: any;
  };
  code_review_depth_chart?: {
    [key: string]: any;
  };
  // Computation status for frontend UX (indicates if data was computed on-the-fly)
  computation_status?: ComputationStatus;
}

export interface TeamInsightCodeReviewAnalyticsQualityChartResponse {
  org_uuid?: string;
  code_review_quality_chart: {
    [key: string]: any;
  };
  date_range: string;
  error?: string | null;
}

export interface TeamInsightCodeReviewAnalyticsTurnaroundChartResponse {
  org_uuid?: string;
  code_review_turnaround_chart: {
    [key: string]: any;
  };
  date_range: string;
  error?: string | null;
}

export interface TeamInsightCodeReviewAnalyticsOutputTypeChartResponse {
  org_uuid?: string;
  code_review_output_type_chart: {
    [key: string]: any;
  };
  date_range: string;
  error?: string | null;
}

export interface TeamInsightCodeReviewAnalyticsDepthChartResponse {
  org_uuid?: string;
  code_review_depth_chart: {
    [key: string]: any;
  };
  date_range: string;
  error?: string | null;
}

export interface TeamInsightExternalAnalyticsResponse {
  org_uuid?: string;
  overall_innovation_ratio: number;
  overall_innovation_ratio_trend?: number;
  innovation_ratio_comparison_text?: string | null; // e.g., "Up from 45.2%" or "Down from 52.3%"
  overall_bug_ratio: number;
  overall_bug_ratio_trend?: number;
  bug_ratio_comparison_text?: string | null; // e.g., "Up from 12.5%" or "Down from 8.3%"
  team_productivity: number;
  team_productivity_trend?: number;
  team_productivity_comparison_text?: string | null; // e.g., "Up from 3.2" or "Down from 4.5"
  reverted_prs_current_week: {
    [key: string]: any;
  };
  reverted_prs_weekly_data: Array<{
    [key: string]: any;
  }>;
  reverted_prs_average_per_week: number;
  reverted_prs_total_reverts: number;
  reverted_prs_total_prs_period: number;
  code_turnover_weekly_data: Array<{
    [key: string]: any;
  }>;
  code_turnover_average_pct: number;
  code_turnover_median_pct: number;
  code_turnover_top10_pct: number;
  code_turnover_total_lines_changed: number;
  code_turnover_total_lines_overwritten: number;
  bug_reports_current_week: {
    [key: string]: any;
  };
  bug_reports_weekly_data: Array<{
    [key: string]: any;
  }>;
  bug_reports_total_created: number;
  bug_reports_total_resolved: number;
  bug_reports_open_count: number;
  bug_reports_average_created_per_week: number;
  bug_reports_average_resolved_per_week: number;
  bug_reports_project_breakdown: Array<{
    [key: string]: any;
  }>;
  bug_reports_error?: string | null;
  date_range: string;
  error?: string | null;
}

// Split external analytics response interfaces (parallel loading)
export interface TeamInsightExternalAnalyticsMetricsResponse {
  org_uuid?: string;
  overall_innovation_ratio: number;
  overall_innovation_ratio_trend?: number;
  innovation_ratio_comparison_text?: string | null;
  overall_bug_ratio: number;
  overall_bug_ratio_trend?: number;
  bug_ratio_comparison_text?: string | null;
  team_productivity: number;
  team_productivity_trend?: number;
  team_productivity_comparison_text?: string | null;
  date_range: string;
  error?: string | null;
  // Computation status for frontend UX (indicates if data was computed on-the-fly)
  computation_status?: ComputationStatus;
}

export interface TeamInsightExternalAnalyticsRevertedPRsResponse {
  org_uuid?: string;
  reverted_prs_current_week: {
    [key: string]: any;
  };
  reverted_prs_weekly_data: Array<{
    [key: string]: any;
  }>;
  reverted_prs_average_per_week: number;
  reverted_prs_total_reverts: number;
  reverted_prs_total_prs_period: number;
  date_range: string;
  error?: string | null;
}

export interface TeamInsightExternalAnalyticsCodeTurnoverResponse {
  org_uuid?: string;
  code_turnover_weekly_data: Array<{
    [key: string]: any;
  }>;
  code_turnover_average_pct: number;
  code_turnover_median_pct: number;
  code_turnover_top10_pct: number;
  code_turnover_total_lines_changed: number;
  code_turnover_total_lines_overwritten: number;
  date_range: string;
  error?: string | null;
}

export interface TeamInsightExternalAnalyticsBugReportsResponse {
  org_uuid?: string;
  bug_reports_current_week: {
    created: number;
    resolved: number;
    week_start: string;
    bug_tickets?: Array<{
      ticket_id: string;
      ticket_number: string;
      title: string;
      url: string;
      status: string;
      project_name: string;
      created_at: string;
      author: string;
      bugs_score: number;
    }>;
  };
  bug_reports_weekly_data: Array<{
    week_start: string;
    created: number;
    resolved: number;
    bug_tickets?: Array<{
      ticket_id: string;
      ticket_number: string;
      title: string;
      url: string;
      status: string;
      project_name: string;
      created_at: string;
      author: string;
      bugs_score: number;
    }>;
  }>;
  bug_reports_total_created: number;
  bug_reports_total_resolved: number;
  bug_reports_open_count: number;
  bug_reports_average_created_per_week: number;
  bug_reports_average_resolved_per_week: number;
  bug_reports_project_breakdown: Array<{
    project_name: string;
    bug_count: number;
    percentage: number;
    bug_tickets?: Array<{
      ticket_id: string;
      ticket_number: string;
      title: string;
      url: string;
      status: string;
      project_name: string;
      created_at: string;
      author: string;
      bugs_score: number;
    }>;
  }>;
  date_range: string;
  error?: string | null;
}

// Optimized metrics summary response (all 7 metrics in one call)
export interface TeamInsightMetricsSummaryResponse {
  org_uuid?: string;
  team_performance: {
    value: number;
    comparison_text: string | null;
  };
  review_quality: {
    value: number;
    comparison_text: string | null;
  };
  review_turnaround: {
    value: number;
    comparison_text: string | null;
  };
  ai_adoption: {
    value: number;
    comparison_text: string | null;
  };
  innovation_ratio: {
    value: number;
    comparison_text: string | null;
  };
  bug_ratio: {
    value: number;
    comparison_text: string | null;
    /** Numeric trend (e.g. from daily external analytics) so card can show change when external hook uses cache */
    trend?: number | null;
  };
  ai_savings: {
    value: number;
    comparison_text: string | null;
  };
  date_range: string;
  error?: string | null;
}

// Cost Charts interfaces
export interface CostEfficiencyMember {
  user_id: string;
  name: string;
  total_loc: number;
  total_cost_usd: number;
  cost_efficiency: number;
  cursor_loc: number;
  cursor_cost_usd: number;
  cursor_efficiency: number;
  claude_loc: number;
  claude_cost_usd: number;
  claude_efficiency: number;
}

export interface CostChartsResponse {
  cost_efficiency_multiplier: CostEfficiencyMember[];
  investment_vs_output: unknown[];
  cost_leaderboard: unknown[];
  total_members: number;
  total_cost_usd: number;
  total_loc: number;
  average_efficiency: number;
  error?: string | null;
}

export const fetchTeamInsightOverview = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightOverviewResponse> => {
  try {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // Increased to 5 minutes timeout

    const response = await postWithAuth(endpoint, payload);

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch team insight overview: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Team insight overview took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching team insight overview:', error);
    throw error;
  }
};

export const fetchCursorTeamInsightOverview = async (
  payload: TeamInsightOverviewRequest
): Promise<CursorTeamInsightOverviewResponse> => {
  try {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // Increased to 2 minutes for complex queries

    const response = await postWithAuth(cursorEndpoint, payload);

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Handle specific HTTP status codes
      if (response.status === 500) {
        throw new Error('Server error - please try again later');
      } else if (response.status === 404) {
        throw new Error('Endpoint not found - please contact support');
      } else if (response.status >= 400 && response.status < 500) {
        throw new Error(
          `Request error: ${response.status} ${response.statusText}`
        );
      } else {
        throw new Error(
          `Failed to fetch cursor team insight overview: ${response.status} ${response.statusText}`
        );
      }
    }

    const data = await response.json();

    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    // Check for error in response
    if (data.error) {
      throw new Error(`Server error: ${data.error}`);
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Cursor team insight overview took too long to respond'
      );
      throw new Error(
        'Request timeout - the query is taking longer than expected. Please try again.'
      );
    } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('Network error:', error);
      throw new Error(
        'Network error - please check your connection and try again'
      );
    } else if (error.message?.includes('broken pipe')) {
      console.error('Connection broken:', error);
      throw new Error('Connection lost - please try again');
    }
    console.error('Error fetching cursor team insight overview:', error);
    throw error;
  }
};

export const fetchTeamInsightPerMember = async (
  payload: TeamInsightPerMemberRequest
): Promise<TeamInsightPerMemberResponse> => {
  try {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    const response = await postWithAuth(teamInsightPerMemberEndpoint, payload);

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch team insight per member: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Team insight per member took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching team insight per member:', error);
    throw error;
  }
};

// New split cursor endpoint functions
export const fetchCursorTeamInsightActivity = async (
  payload: TeamInsightOverviewRequest
): Promise<CursorTeamInsightActivityResponse> => {
  try {
    const controller = new AbortController();
    // 180s: this endpoint runs 5+ minutes server-side right now (backend fix in progress).
    // The BFF proxy itself aborts at 170s and returns a clean 504, so keep this client
    // timeout just above that ceiling -- a safety net for a hung proxy, not a race against it.
    const timeoutId = setTimeout(() => controller.abort(), 180000);

    // Ensure integration parameter is set based on the endpoint
    const requestPayload = {
      ...payload,
      integration: payload.integration || 'cursor', // Default to cursor for this endpoint
    };

    const response = await postWithAuth(
      cursorActivityEndpoint,
      requestPayload,
      {
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch cursor team insight activity: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    if (data.error) {
      throw new Error(`Server error: ${data.error}`);
    }

    // Normalize activity chart: backend may send snake_case or different keys
    if (Array.isArray(data.ai_code_activity_chart)) {
      data.ai_code_activity_chart = data.ai_code_activity_chart.map(
        (item: Record<string, unknown>) => {
          const raw = item as Record<string, unknown>;
          let aiAdditions = Number(
            raw.aiAdditions ??
              raw.ai_additions ??
              raw.assisted_additions ??
              raw.ai_generated ??
              raw.ai_assisted ??
              raw.ai_loc ??
              raw.generated_additions ??
              // integration-specific variants seen in some backends
              raw.cursor_ai_additions ??
              raw.cursorAiAdditions ??
              raw.cursor_ai_adds ??
              raw.cursor_ai_lines_added ??
              raw.claude_ai_additions ??
              raw.claudeAiAdditions ??
              raw.claude_ai_adds ??
              raw.claude_ai_lines_added ??
              raw.ai_additions_cursor ??
              raw.ai_additions_claude ??
              0
          );

          // Last-resort heuristic: some backends rename fields; pick any numeric key that looks like AI additions.
          // This keeps the chart working without waiting on backend naming consistency.
          if (!aiAdditions) {
            const aiAdditionCandidate = Object.entries(raw).find(
              ([key, value]) => {
                if (value === null || value === undefined) return false;
                const n = typeof value === 'number' ? value : Number(value);
                return (
                  Number.isFinite(n) &&
                  n > 0 &&
                  /ai/i.test(key) &&
                  /add/i.test(key)
                );
              }
            );
            if (aiAdditionCandidate) {
              const [, value] = aiAdditionCandidate;
              aiAdditions = typeof value === 'number' ? value : Number(value);
            }
          }
          const humanAdditions = Number(
            raw.human_additions ?? raw.humanAdditions ?? 0
          );
          const totalAdditions = Number(
            raw.additions ?? raw.total_additions ?? 0
          );
          const additions =
            totalAdditions > 0 ? totalAdditions : humanAdditions + aiAdditions;
          const deletions = Number(raw.deletions ?? 0);
          const aiDeletions = Number(raw.aiDeletions ?? raw.ai_deletions ?? 0);
          return {
            date: String(raw.date ?? ''),
            additions,
            deletions,
            aiAdditions,
            aiDeletions,
          };
        }
      );
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Cursor team insight activity took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching cursor team insight activity:', error);
    throw error;
  }
};

export const fetchCursorTeamInsightMetrics = async (
  payload: TeamInsightOverviewRequest
): Promise<CursorTeamInsightMetricsResponse> => {
  try {
    // Convert date_range/date_interval to start_date/end_date for daily endpoint
    const { start_date, end_date } = convertDateRangeToStartEnd(
      payload.date_range,
      payload.date_interval
    );

    // Daily endpoint accepts team_uuids array or undefined for all teams
    // Also pass integration parameter if specified
    const newPayload: any = {
      org_uuid: payload.org_uuid,
      team_uuids:
        payload.team_uuids && payload.team_uuids.length > 0
          ? payload.team_uuids
          : undefined, // Pass array or undefined for all teams
      start_date,
      end_date,
    };

    // Include integration parameter if specified (cursor or claude)
    if (payload.integration) {
      newPayload.integration = payload.integration;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // Reduced timeout (30s instead of 60s) - daily endpoints are faster

    let response;
    let data;
    let useFallback = false;

    try {
      response = await postWithAuth(cursorMetricsEndpoint, newPayload, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        // If daily endpoint fails, fallback to old endpoint
        useFallback = true;
      } else {
        data = await response.json();

        if (!data || typeof data !== 'object') {
          useFallback = true;
        } else if (data.error) {
          // Check if error indicates daily table not available
          const errorMsg = data.error.toLowerCase();
          if (
            errorMsg.includes('daily') ||
            errorMsg.includes('table') ||
            errorMsg.includes('computation') ||
            errorMsg.includes('not available')
          ) {
            useFallback = true;
          } else {
            throw new Error(`Server error: ${data.error}`);
          }
        } else if (
          data.computation_status?.needs_computation &&
          (!data.ai_adoption || data.ai_adoption === 0) &&
          (!data.ai_savings || data.ai_savings === 0)
        ) {
          // If computation is needed and no data available, fallback to old endpoint
          useFallback = true;
        }
      }
    } catch (dailyError: any) {
      // If daily endpoint throws an error, fallback to old endpoint
      console.warn(
        '[fetchCursorTeamInsightMetrics] Daily endpoint failed, falling back to old endpoint:',
        dailyError
      );
      useFallback = true;
    }

    // Fallback to old endpoint if daily endpoint failed or data not available.
    // Cursor only: the old endpoint takes minutes server-side, and it maps every
    // integration other than 'claude' onto Cursor data, so a codex fallback would
    // return the wrong provider. Other integrations rely on the daily table; a
    // "needs computation" daily response renders as empty, a failed one errors
    // out below instead of hanging for the fallback's 180s ceiling.
    const oldEndpointApplies =
      !payload.integration || payload.integration === 'cursor';
    if (useFallback && oldEndpointApplies) {
      console.log(
        '[fetchCursorTeamInsightMetrics] Using fallback endpoint:',
        cursorMetricsEndpointFallback
      );
      const fallbackController = new AbortController();
      // 180s: the old endpoint runs 5+ minutes server-side right now (backend fix in
      // progress). The BFF proxy aborts at 170s and returns a clean 504, so keep this
      // client timeout just above that ceiling -- a safety net for a hung proxy, not a
      // race against it.
      const fallbackTimeoutId = setTimeout(
        () => fallbackController.abort(),
        180000
      );

      // Ensure integration parameter is set for fallback
      const fallbackPayload = {
        ...payload,
        integration: payload.integration || 'cursor', // Default to cursor for this endpoint
      };

      response = await postWithAuth(
        cursorMetricsEndpointFallback,
        fallbackPayload,
        { signal: fallbackController.signal }
      );
      clearTimeout(fallbackTimeoutId);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch cursor team insight metrics (fallback): ${response.status} ${response.statusText}`
        );
      }

      data = await response.json();

      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from server (fallback)');
      }

      if (data.error) {
        throw new Error(`Server error (fallback): ${data.error}`);
      }

      // Return old endpoint response format directly
      return data;
    }

    if (!data || typeof data !== 'object') {
      throw new Error(
        `Daily AI analytics unavailable for integration ${payload.integration}`
      );
    }

    if (data.error) {
      throw new Error(`Server error: ${data.error}`);
    }

    // Show toast if computation is needed (with deduplication)
    if (data.computation_status?.needs_computation) {
      showComputationToastIfNeeded();
    }

    // Transform daily endpoint response to match expected format (backward compatibility)
    return {
      org_uuid: data.org_uuid,
      teams_with_cursor_data: data.teams_with_cursor_data ?? [],
      teams_without_cursor_data: data.teams_without_cursor_data ?? [],
      ai_adoption: data.ai_adoption ?? 0,
      overall_ai_savings: data.ai_savings ?? 0,
      ai_adoption_comparison_text:
        data.ai_adoption_comparison_text ?? undefined,
      ai_savings_comparison_text: data.ai_savings_comparison_text ?? undefined,
      ai_adoption_trend: data.ai_adoption_trend ?? undefined,
      ai_savings_trend: data.ai_savings_trend ?? undefined,
      ai_savings_breakdown: data.ai_savings_breakdown,
      ai_usage_per_member: data.ai_usage_per_member || [], // Use ai_usage_per_member from daily endpoint
      total_teams_with_cursor:
        (data.teams_with_cursor_data ?? []).length ||
        (typeof data.total_teams_with_cursor === 'number'
          ? data.total_teams_with_cursor
          : 0),
      total_teams_without_cursor:
        (data.teams_without_cursor_data ?? []).length ||
        (typeof data.total_teams_without_cursor === 'number'
          ? data.total_teams_without_cursor
          : 0),
      date_range: payload.date_range, // Keep for backward compatibility
      team_summaries: undefined,
      ai_code_contribution:
        data.ai_code_contribution ||
        (data.ai_adoption
          ? {
              overall_percentage: data.ai_adoption,
              total_lines_added: 0,
              total_lines_deleted: 0,
              ai_lines_added: 0,
              ai_lines_deleted: 0,
            }
          : undefined),
      error: data.error,
      // Include computation status for frontend UX
      computation_status: data.computation_status,
      daily_data: data.daily_data ?? [],
    };
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Cursor team insight metrics took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching cursor team insight metrics:', error);
    throw error;
  }
};

export const fetchCursorTeamInsightOutput = async (
  payload: TeamInsightOverviewRequest
): Promise<CursorTeamInsightOutputResponse> => {
  try {
    const controller = new AbortController();
    // 180s: this endpoint runs 5+ minutes server-side right now (backend fix in progress).
    // The BFF proxy itself aborts at 170s and returns a clean 504, so keep this client
    // timeout just above that ceiling -- a safety net for a hung proxy, not a race against it.
    const timeoutId = setTimeout(() => controller.abort(), 180000);

    // Ensure integration parameter is set based on the endpoint
    const requestPayload = {
      ...payload,
      integration: payload.integration || 'cursor', // Default to cursor for this endpoint
    };

    const response = await postWithAuth(cursorOutputEndpoint, requestPayload, {
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch cursor team insight output: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    if (data.error) {
      throw new Error(`Server error: ${data.error}`);
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Cursor team insight output took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching cursor team insight output:', error);
    throw error;
  }
};

// New optimized endpoint fetch functions
/**
 * Whether a request must skip the pre-computed daily-analytics endpoint and go
 * straight to the live one.
 *
 * The `ti_daily_*` tables are keyed (org_uuid, team_uuid, date) and store
 * metrics already averaged over every author in that team-day, so there is no
 * per-person row a paid-seat filter could select. The backend refuses such a
 * request rather than answering it with org-wide numbers under a "paid seats"
 * label; skipping the attempt here just saves the round trip.
 */
export const shouldSkipDailyEndpoint = (payload: {
  seats_only?: boolean;
}): boolean => Boolean(payload.seats_only);

export const fetchTeamInsightCoreAnalytics = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightCoreAnalyticsResponse> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout

    // Unlike the Teams fetcher below, this one has no daily/live fallback
    // chain — so the endpoint has to be chosen up front. Posting a
    // seat-filtered payload at the daily endpoint would be refused and throw,
    // losing the tab's core data entirely.
    const response = await postWithAuth(
      shouldSkipDailyEndpoint(payload)
        ? teamInsightCoreAnalyticsEndpointLive
        : teamInsightCoreAnalyticsEndpoint,
      payload
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch team insight core analytics: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    // Preserve computation_status if present
    return {
      ...data,
      computation_status: data.computation_status,
    };
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Team insight core analytics took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching team insight core analytics:', error);
    throw error;
  }
};

export const fetchTeamInsightCoreAnalyticsTeams = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightCoreAnalyticsTeamsResponse> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout

    let response;
    let data;
    // Seat-filtered reads cannot come from the daily tables - go live.
    let useFallback = shouldSkipDailyEndpoint(payload);
    if (useFallback) {
      // Nothing will consume the daily attempt's abort timer.
      clearTimeout(timeoutId);
    }

    if (!useFallback) {
      try {
        response = await postWithAuth(
          teamInsightCoreAnalyticsTeamsEndpoint,
          payload
        );
        clearTimeout(timeoutId);

        if (!response.ok) {
          // If daily endpoint fails, fallback to old endpoint
          useFallback = true;
        } else {
          data = await response.json();

          if (!data || typeof data !== 'object') {
            useFallback = true;
          } else if (data.error) {
            // Check if error indicates daily table not available
            const errorMsg = data.error.toLowerCase();
            if (
              errorMsg.includes('daily') ||
              errorMsg.includes('table') ||
              errorMsg.includes('computation') ||
              errorMsg.includes('not available')
            ) {
              useFallback = true;
            } else {
              throw new Error(`Server error: ${data.error}`);
            }
          } else if (
            data.computation_status?.needs_computation &&
            (!data.team_insights || data.team_insights.length === 0) &&
            (!data.overall_team_performance_average ||
              data.overall_team_performance_average === 0)
          ) {
            // If computation is needed and no data available, fallback to old endpoint
            useFallback = true;
          }
        }
      } catch (dailyError: unknown) {
        // If daily endpoint throws an error, fallback to old endpoint
        console.warn(
          '[fetchTeamInsightCoreAnalyticsTeams] Daily endpoint failed, falling back to old endpoint:',
          dailyError
        );
        useFallback = true;
      }
    }

    // Fallback to old endpoint if daily endpoint failed or data not available
    if (useFallback) {
      console.log(
        '[fetchTeamInsightCoreAnalyticsTeams] Using fallback endpoint:',
        teamInsightCoreAnalyticsTeamsEndpointFallback
      );
      const fallbackController = new AbortController();
      const fallbackTimeoutId = setTimeout(
        () => fallbackController.abort(),
        300000
      ); // 5 minutes timeout for old endpoint

      response = await postWithAuth(
        teamInsightCoreAnalyticsTeamsEndpointFallback,
        payload
      );
      clearTimeout(fallbackTimeoutId);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch team insight core analytics teams (fallback): ${response.status} ${response.statusText}`
        );
      }

      data = await response.json();

      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from server (fallback)');
      }

      if (data.error) {
        throw new Error(`Server error (fallback): ${data.error}`);
      }

      // Return old endpoint response format directly
      return data;
    }

    // Show toast if computation is needed (with deduplication)
    if (data.computation_status?.needs_computation) {
      showComputationToastIfNeeded();
    }

    // Preserve computation_status if present
    return {
      ...data,
      computation_status: data.computation_status,
    };
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Team insight core analytics teams took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching team insight core analytics teams:', error);
    throw error;
  }
};

// Deprecated - team chart is now included in dailyCoreAnalytics endpoint
export const fetchTeamInsightCoreAnalyticsTeamChart = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightCoreAnalyticsTeamChartResponse> => {
  // Redirect to main endpoint which includes all charts
  const data = await fetchTeamInsightCoreAnalyticsTeams(payload);
  return {
    org_uuid: data.org_uuid,
    output_per_team_chart: data.output_per_team_chart || {},
    date_range: data.date_range,
    error: data.error,
  } as TeamInsightCoreAnalyticsTeamChartResponse;
};

// Deprecated - engineer chart is now included in dailyCoreAnalytics endpoint
export const fetchTeamInsightCoreAnalyticsEngineerChart = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightCoreAnalyticsEngineerChartResponse> => {
  // Redirect to main endpoint which includes all charts
  const data = await fetchTeamInsightCoreAnalyticsTeams(payload);
  return {
    org_uuid: data.org_uuid,
    output_per_engineer_chart: data.output_per_engineer_chart || {},
    date_range: data.date_range,
    error: data.error,
  } as TeamInsightCoreAnalyticsEngineerChartResponse;
};

export const fetchTeamInsightCodeReviewAnalytics = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightCodeReviewAnalyticsResponse> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout

    const response = await postWithAuth(
      teamInsightCodeReviewAnalyticsEndpoint,
      payload
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch team insight code review analytics: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Team insight code review analytics took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching team insight code review analytics:', error);
    throw error;
  }
};

// Split code review analytics API functions (parallel loading)
// Helper function to convert date_range/date_interval to start_date/end_date
function convertDateRangeToStartEnd(
  dateRange: string,
  dateInterval?: { start_date: string; end_date: string }
): { start_date: string; end_date: string } {
  if (dateInterval?.start_date && dateInterval?.end_date) {
    return {
      start_date: dateInterval.start_date,
      end_date: dateInterval.end_date,
    };
  }

  const today = new Date();

  const startDate = new Date(today);

  switch (dateRange) {
    case 'weekly':
      startDate.setDate(today.getDate() - 7);
      break;
    case 'monthly':
      startDate.setDate(today.getDate() - 30);
      break;
    case '3months':
      startDate.setDate(today.getDate() - 90);
      break;
    case 'yearly':
      startDate.setDate(today.getDate() - 365);
      break;
    default:
      startDate.setDate(today.getDate() - 14); // Default to 14 days
  }

  startDate.setHours(0, 0, 0, 0);

  return {
    start_date: formatDateToLocalYYYYMMDD(startDate),
    end_date: formatDateToLocalYYYYMMDD(today),
  };
}

export const fetchTeamInsightCodeReviewAnalyticsMetrics = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightCodeReviewAnalyticsMetricsResponse> => {
  try {
    // Convert date_range/date_interval to start_date/end_date for daily endpoint
    const { start_date, end_date } = convertDateRangeToStartEnd(
      payload.date_range,
      payload.date_interval
    );

    // Daily endpoint accepts team_uuids array or undefined for all teams
    const newPayload = {
      org_uuid: payload.org_uuid,
      team_uuids:
        payload.team_uuids && payload.team_uuids.length > 0
          ? payload.team_uuids
          : undefined, // Pass array or undefined for all teams
      start_date,
      end_date,
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // Reduced timeout (30s instead of 120s) - daily endpoints are faster

    let response;
    let data;
    // Seat-filtered reads cannot come from the daily tables - go live.
    let useFallback = shouldSkipDailyEndpoint(payload);
    if (useFallback) {
      // Nothing will consume the daily attempt's abort timer.
      clearTimeout(timeoutId);
    }

    if (!useFallback) {
      try {
        response = await postWithAuth(
          teamInsightCodeReviewAnalyticsMetricsEndpoint,
          newPayload
        );
        clearTimeout(timeoutId);

        if (!response.ok) {
          // If daily endpoint fails, fallback to old endpoint
          useFallback = true;
        } else {
          data = await response.json();

          if (!data || typeof data !== 'object') {
            useFallback = true;
          } else if (data.error) {
            // Check if error indicates daily table not available
            const errorMsg = data.error.toLowerCase();
            if (
              errorMsg.includes('daily') ||
              errorMsg.includes('table') ||
              errorMsg.includes('computation') ||
              errorMsg.includes('not available')
            ) {
              useFallback = true;
            } else {
              throw new Error(`Server error: ${data.error}`);
            }
          } else if (
            data.computation_status?.needs_computation &&
            (!data.avg_review_quality || data.avg_review_quality === 0) &&
            (!data.median_review_turnaround ||
              data.median_review_turnaround === 0)
          ) {
            // If computation is needed and no data available, fallback to old endpoint
            useFallback = true;
          }
        }
      } catch (dailyError: unknown) {
        // If daily endpoint throws an error, fallback to old endpoint
        console.warn(
          '[fetchTeamInsightCodeReviewAnalyticsMetrics] Daily endpoint failed, falling back to old endpoint:',
          dailyError
        );
        useFallback = true;
      }
    }

    // Fallback to old endpoint if daily endpoint failed or data not available
    if (useFallback) {
      console.log(
        '[fetchTeamInsightCodeReviewAnalyticsMetrics] Using fallback endpoint:',
        teamInsightCodeReviewAnalyticsMetricsEndpointFallback
      );
      const fallbackController = new AbortController();
      const fallbackTimeoutId = setTimeout(
        () => fallbackController.abort(),
        120000
      ); // 2 minutes timeout for old endpoint

      response = await postWithAuth(
        teamInsightCodeReviewAnalyticsMetricsEndpointFallback,
        payload
      );
      clearTimeout(fallbackTimeoutId);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch code review analytics metrics (fallback): ${response.status} ${response.statusText}`
        );
      }

      data = await response.json();

      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from server (fallback)');
      }

      if (data.error) {
        throw new Error(`Server error (fallback): ${data.error}`);
      }

      // Return old endpoint response format directly
      return data;
    }

    // Show toast if computation is needed (with deduplication)
    if (data.computation_status?.needs_computation) {
      showComputationToastIfNeeded();
    }

    // Transform daily endpoint response to match expected format (backward compatibility)
    // Include chart data from daily endpoint for use by chart hooks
    return {
      org_uuid: data.org_uuid,
      overall_avg_review_quality: data.avg_review_quality ?? 0,
      // Coverage travels beside quality. `avg_review_quality` is an average over
      // REVIEWED PRs, so alone it says nothing about how many that was — the old
      // number folded both together and read as quality when it was quality x
      // coverage. Null-preserving on purpose: rows predating the backend's
      // reviewed_prs column are genuinely unknown, and `?? 0` would render that
      // as "0% reviewed", which is the conflation this is meant to remove.
      review_coverage: data.review_coverage ?? null,
      reviewed_prs: data.reviewed_prs ?? null,
      total_prs: data.total_prs ?? null,
      overall_median_review_turnaround: data.median_review_turnaround ?? 0,
      quality_comparison_text: data.quality_comparison_text,
      turnaround_comparison_text: data.turnaround_comparison_text,
      overall_avg_review_quality_trend: data.quality_trend ?? undefined,
      overall_median_review_turnaround_trend:
        data.turnaround_trend ?? undefined,
      date_range: payload.date_range, // Keep for backward compatibility
      error: data.error,
      // Include chart data from daily endpoint
      code_review_quality_chart: data.code_review_quality_chart,
      code_review_turnaround_chart: data.code_review_turnaround_chart,
      code_review_depth_chart: data.code_review_depth_chart,
      // Include computation status for frontend UX
      computation_status: data.computation_status,
    };
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Code review analytics metrics took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching code review analytics metrics:', error);
    throw error;
  }
};

/**
 * @deprecated Use chart data from fetchTeamInsightCodeReviewAnalyticsMetrics instead
 * This function now extracts chart data from the daily endpoint response via the metrics hook
 */
export const fetchTeamInsightCodeReviewAnalyticsQualityChart = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightCodeReviewAnalyticsQualityChartResponse> => {
  // Use the daily endpoint which already includes chart data
  // This maintains backward compatibility while using optimized endpoint
  try {
    const metricsData =
      await fetchTeamInsightCodeReviewAnalyticsMetrics(payload);

    return {
      org_uuid: metricsData.org_uuid,
      code_review_quality_chart: metricsData.code_review_quality_chart || {},
      date_range: metricsData.date_range,
      error: metricsData.error,
    };
  } catch (error: any) {
    console.error('Error fetching code review quality chart:', error);
    throw error;
  }
};

/**
 * @deprecated Use chart data from fetchTeamInsightCodeReviewAnalyticsMetrics instead
 * This function now extracts chart data from the daily endpoint response via the metrics hook
 */
export const fetchTeamInsightCodeReviewAnalyticsTurnaroundChart = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightCodeReviewAnalyticsTurnaroundChartResponse> => {
  // Use the daily endpoint which already includes chart data
  // This maintains backward compatibility while using optimized endpoint
  try {
    const metricsData =
      await fetchTeamInsightCodeReviewAnalyticsMetrics(payload);

    return {
      org_uuid: metricsData.org_uuid,
      code_review_turnaround_chart:
        metricsData.code_review_turnaround_chart || {},
      date_range: metricsData.date_range,
      error: metricsData.error,
    };
  } catch (error: any) {
    console.error('Error fetching code review turnaround chart:', error);
    throw error;
  }
};

export const fetchTeamInsightCodeReviewAnalyticsOutputTypeChart = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightCodeReviewAnalyticsOutputTypeChartResponse> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minutes timeout

    const response = await postWithAuth(
      teamInsightCodeReviewAnalyticsOutputTypeChartEndpoint,
      payload
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch code review output type chart: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Code review output type chart took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching code review output type chart:', error);
    throw error;
  }
};

/**
 * @deprecated Use chart data from fetchTeamInsightCodeReviewAnalyticsMetrics instead
 * This function now extracts chart data from the daily endpoint response via the metrics hook
 */
export const fetchTeamInsightCodeReviewAnalyticsDepthChart = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightCodeReviewAnalyticsDepthChartResponse> => {
  // Use the daily endpoint which already includes chart data
  // This maintains backward compatibility while using optimized endpoint
  try {
    const metricsData =
      await fetchTeamInsightCodeReviewAnalyticsMetrics(payload);

    return {
      org_uuid: metricsData.org_uuid,
      code_review_depth_chart: metricsData.code_review_depth_chart || {},
      date_range: metricsData.date_range,
      error: metricsData.error,
    };
  } catch (error: any) {
    console.error('Error fetching code review depth chart:', error);
    throw error;
  }
};

export const fetchTeamInsightExternalAnalytics = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightExternalAnalyticsResponse> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout

    const response = await postWithAuth(
      teamInsightExternalAnalyticsEndpoint,
      payload
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch team insight external analytics: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Team insight external analytics took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching team insight external analytics:', error);
    throw error;
  }
};

// Split external analytics API functions (parallel loading)
export const fetchTeamInsightExternalAnalyticsMetrics = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightExternalAnalyticsMetricsResponse> => {
  try {
    // Convert date_range/date_interval to start_date/end_date for daily endpoint
    const { start_date, end_date } = convertDateRangeToStartEnd(
      payload.date_range,
      payload.date_interval
    );

    // Daily endpoint accepts team_uuids array or undefined for all teams
    const newPayload = {
      org_uuid: payload.org_uuid,
      team_uuids:
        payload.team_uuids && payload.team_uuids.length > 0
          ? payload.team_uuids
          : undefined, // Pass array or undefined for all teams
      start_date,
      end_date,
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // Reduced timeout (30s instead of 300s) - daily endpoints are faster

    let response;
    let data;
    let useFallback = false;

    try {
      response = await postWithAuth(
        teamInsightExternalAnalyticsMetricsEndpoint,
        newPayload
      );
      clearTimeout(timeoutId);

      if (!response.ok) {
        // If daily endpoint fails, fallback to old endpoint
        useFallback = true;
      } else {
        data = await response.json();

        if (!data || typeof data !== 'object') {
          useFallback = true;
        } else if (data.error) {
          // Check if error indicates daily table not available
          const errorMsg = data.error.toLowerCase();
          if (
            errorMsg.includes('daily') ||
            errorMsg.includes('table') ||
            errorMsg.includes('computation') ||
            errorMsg.includes('not available')
          ) {
            useFallback = true;
          } else {
            throw new Error(`Server error: ${data.error}`);
          }
        } else if (
          data.computation_status?.needs_computation &&
          (!data.innovation_ratio || data.innovation_ratio === 0) &&
          (!data.bug_ratio || data.bug_ratio === 0)
        ) {
          // If computation is needed and no data available, fallback to old endpoint
          useFallback = true;
        }
      }
    } catch (dailyError: any) {
      // If daily endpoint throws an error, fallback to old endpoint
      console.warn(
        '[fetchTeamInsightExternalAnalyticsMetrics] Daily endpoint failed, falling back to old endpoint:',
        dailyError
      );
      useFallback = true;
    }

    // Fallback to old endpoint if daily endpoint failed or data not available
    if (useFallback) {
      console.log(
        '[fetchTeamInsightExternalAnalyticsMetrics] Using fallback endpoint:',
        teamInsightExternalAnalyticsMetricsEndpointFallback
      );
      const fallbackController = new AbortController();
      const fallbackTimeoutId = setTimeout(
        () => fallbackController.abort(),
        300000
      ); // 5 minutes timeout for old endpoint

      response = await postWithAuth(
        teamInsightExternalAnalyticsMetricsEndpointFallback,
        payload
      );
      clearTimeout(fallbackTimeoutId);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch team insight external analytics metrics (fallback): ${response.status} ${response.statusText}`
        );
      }

      data = await response.json();

      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from server (fallback)');
      }

      if (data.error) {
        throw new Error(`Server error (fallback): ${data.error}`);
      }

      // Return old endpoint response format directly
      return data;
    }

    // Show toast if computation is needed (with deduplication)
    if (data.computation_status?.needs_computation) {
      showComputationToastIfNeeded();
    }

    // Transform daily endpoint response to match expected format (backward compatibility)
    return {
      org_uuid: data.org_uuid,
      overall_innovation_ratio: data.innovation_ratio ?? 0,
      overall_innovation_ratio_trend: data.innovation_ratio_trend ?? undefined,
      innovation_ratio_comparison_text: data.innovation_comparison_text,
      overall_bug_ratio: data.bug_ratio ?? 0,
      overall_bug_ratio_trend: data.bug_ratio_trend ?? undefined,
      bug_ratio_comparison_text: data.bug_comparison_text,
      team_productivity: data.team_productivity ?? 0,
      team_productivity_trend: data.team_productivity_trend ?? undefined,
      team_productivity_comparison_text:
        data.team_productivity_comparison_text ?? undefined,
      date_range: payload.date_range, // Keep for backward compatibility
      error: data.error,
      // Include computation status for frontend UX
      computation_status: data.computation_status,
    };
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Team insight external analytics metrics took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error(
      'Error fetching team insight external analytics metrics:',
      error
    );
    throw error;
  }
};

export const fetchTeamInsightExternalAnalyticsRevertedPRs = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightExternalAnalyticsRevertedPRsResponse> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout

    const response = await postWithAuth(
      teamInsightExternalAnalyticsRevertedPRsEndpoint,
      payload
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch team insight external analytics reverted PRs: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Team insight external analytics reverted PRs took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error(
      'Error fetching team insight external analytics reverted PRs:',
      error
    );
    throw error;
  }
};

export const fetchTeamInsightExternalAnalyticsCodeTurnover = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightExternalAnalyticsCodeTurnoverResponse> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout

    const response = await postWithAuth(
      teamInsightExternalAnalyticsCodeTurnoverEndpoint,
      payload
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch team insight external analytics code turnover: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Team insight external analytics code turnover took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error(
      'Error fetching team insight external analytics code turnover:',
      error
    );
    throw error;
  }
};

export const fetchTeamInsightExternalAnalyticsBugReports = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightExternalAnalyticsBugReportsResponse> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout

    const response = await postWithAuth(
      teamInsightExternalAnalyticsBugReportsEndpoint,
      payload
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch team insight external analytics bug reports: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    // The API uses HTTP 200 for domain-level failures. Promote its explicit
    // section error to React Query so the chart can distinguish failure from 0.
    if (data.error) {
      throw new Error('Bug reports are temporarily unavailable');
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Team insight external analytics bug reports took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error(
      'Error fetching team insight external analytics bug reports:',
      error
    );
    throw error;
  }
};

// Optimized metrics summary endpoint - aggregates from 4 daily endpoints
// This replaces the backend meta-endpoint with direct calls to optimized daily aggregation tables
const fetchTeamInsightMetricsSummary = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightMetricsSummaryResponse> => {
  try {
    console.log(
      '[Metrics Summary] Fetching metrics summary from daily endpoints:',
      {
        payload: {
          org_uuid: payload.org_uuid,
          team_uuids: payload.team_uuids,
          date_range: payload.date_range,
          date_interval: payload.date_interval,
          integration: payload.integration,
        },
      }
    );

    // Call all 4 daily endpoints in parallel for maximum performance
    // These endpoints use pre-computed daily aggregation tables (much faster than on-the-fly calculations)
    const [coreData, codeReviewData, externalData, aiData] = await Promise.all([
      fetchTeamInsightCoreAnalyticsTeams(payload).catch((err) => {
        console.warn('[Metrics Summary] Error fetching core analytics:', err);
        return null;
      }),
      fetchTeamInsightCodeReviewAnalyticsMetrics(payload).catch((err) => {
        console.warn(
          '[Metrics Summary] Error fetching code review analytics:',
          err
        );
        return null;
      }),
      fetchTeamInsightExternalAnalyticsMetrics(payload).catch((err) => {
        console.warn(
          '[Metrics Summary] Error fetching external analytics:',
          err
        );
        return null;
      }),
      fetchCursorTeamInsightMetrics(payload).catch((err) => {
        console.warn('[Metrics Summary] Error fetching AI analytics:', err);
        return null;
      }),
    ]);

    // Aggregate into metrics summary format
    const result: TeamInsightMetricsSummaryResponse = {
      org_uuid: payload.org_uuid,
      date_range: payload.date_range,
      team_performance: {
        value: coreData?.overall_team_performance_average ?? 0,
        comparison_text: coreData?.overall_comparison_text ?? null,
      },
      review_quality: {
        value: codeReviewData?.overall_avg_review_quality ?? 0,
        comparison_text: codeReviewData?.quality_comparison_text ?? null,
      },
      review_turnaround: {
        value: codeReviewData?.overall_median_review_turnaround ?? 0,
        comparison_text: codeReviewData?.turnaround_comparison_text ?? null,
      },
      innovation_ratio: {
        value: externalData?.overall_innovation_ratio ?? 0,
        comparison_text: externalData?.innovation_ratio_comparison_text ?? null,
      },
      bug_ratio: {
        value: externalData?.overall_bug_ratio ?? 0,
        comparison_text: externalData?.bug_ratio_comparison_text ?? null,
      },
      ai_adoption: {
        value: aiData?.ai_adoption ?? 0,
        comparison_text: aiData?.ai_adoption_comparison_text ?? null,
      },
      ai_savings: {
        value: aiData?.overall_ai_savings ?? 0,
        comparison_text: aiData?.ai_savings_comparison_text ?? null,
      },
      error: null,
    };

    // Check if any critical endpoint failed
    const errors: string[] = [];
    if (!coreData) errors.push('Core analytics');
    if (!codeReviewData) errors.push('Code review analytics');
    if (!externalData) errors.push('External analytics');
    if (!aiData) errors.push('AI analytics');

    if (errors.length > 0) {
      result.error = `Failed to fetch: ${errors.join(', ')}`;
      console.warn('[Metrics Summary] Some endpoints failed:', errors);
    }

    console.log('[Metrics Summary] Successfully aggregated metrics summary:', {
      team_performance: result.team_performance,
      review_quality: result.review_quality,
      review_turnaround: result.review_turnaround,
      ai_adoption: result.ai_adoption,
      innovation_ratio: result.innovation_ratio,
      bug_ratio: result.bug_ratio,
      ai_savings: result.ai_savings,
    });

    return result;
  } catch (error: any) {
    console.error('Error fetching team insight metrics summary:', error);
    return {
      org_uuid: payload.org_uuid,
      date_range: payload.date_range,
      team_performance: { value: 0, comparison_text: null },
      review_quality: { value: 0, comparison_text: null },
      review_turnaround: { value: 0, comparison_text: null },
      ai_adoption: { value: 0, comparison_text: null },
      innovation_ratio: { value: 0, comparison_text: null },
      bug_ratio: { value: 0, comparison_text: null },
      ai_savings: { value: 0, comparison_text: null },
      error: error.message || 'Unknown error',
    };
  }
};

// Cost Charts endpoint
export const fetchCostCharts = async (
  payload: TeamInsightOverviewRequest
): Promise<CostChartsResponse> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 1 minute timeout

    const response = await postWithAuth(costChartsEndpoint, payload);

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch cost charts: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    if (data.error) {
      throw new Error(`Server error: ${data.error}`);
    }

    return {
      cost_efficiency_multiplier: data.cost_efficiency_multiplier || [],
      investment_vs_output: data.investment_vs_output || [],
      cost_leaderboard: data.cost_leaderboard || [],
      total_members: data.total_members || 0,
      total_cost_usd: data.total_cost_usd || 0,
      total_loc: data.total_loc || 0,
      average_efficiency: data.average_efficiency || 0,
      error: null,
    };
  } catch (error: any) {
    console.error('Error fetching cost charts:', error);
    return {
      cost_efficiency_multiplier: [],
      investment_vs_output: [],
      cost_leaderboard: [],
      total_members: 0,
      total_cost_usd: 0,
      total_loc: 0,
      average_efficiency: 0,
      error: error.message || 'Unknown error',
    };
  }
};

// Work Type Per Person interfaces
export interface PersonWorkTypeData {
  user_id: string;
  name: string;
  email?: string | null;
  image?: string | null;
  bugs_percentage: number;
  ktlo_percentage: number;
  features_percentage: number;
  innovation_percentage: number;
  total_prs: number;
}

export interface TeamInsightWorkTypePerPersonResponse {
  org_uuid?: string;
  person_data: PersonWorkTypeData[];
  total_persons: number;
  start_date: string;
  end_date: string;
  error?: string | null;
}

// Work Type Per Person API function
export const fetchWorkTypePerPerson = async (
  payload: TeamInsightOverviewRequest
): Promise<TeamInsightWorkTypePerPersonResponse> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    const response = await postWithAuth(workTypePerPersonEndpoint, payload);

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch work type per person: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    if (data.error) {
      throw new Error(`Server error: ${data.error}`);
    }

    return {
      org_uuid: data.org_uuid,
      person_data: data.person_data || [],
      total_persons: data.total_persons || 0,
      start_date: data.start_date || '',
      end_date: data.end_date || '',
      error: null,
    };
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Work type per person took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error fetching work type per person:', error);
    return {
      person_data: [],
      total_persons: 0,
      start_date: '',
      end_date: '',
      error: error.message || 'Unknown error',
    };
  }
};

// Compute Daily Analytics Functions
export interface ComputeDailyAnalyticsRequest {
  org_uuid: string;
  team_uuid?: string | null;
  date?: string;
  start_date?: string;
  end_date?: string;
  integration?: 'cursor' | 'claude' | 'codex';
}

export interface ComputeDailyAnalyticsResponse {
  status: string;
  date?: string;
  start_date?: string;
  end_date?: string;
  total_days?: number;
  successful_days?: number;
  failed_days?: number;
  results_by_date?: Record<string, any>;
  teams_processed?: number;
  results?: Array<{
    team_uuid: string;
    team_name: string;
    team_average: number;
    total_engineers: number;
    total_prs: number;
  }>;
  error?: string;
}

const computeDailyAIAnalytics = async (
  payload: ComputeDailyAnalyticsRequest
): Promise<ComputeDailyAnalyticsResponse> => {
  try {
    if (!payload.org_uuid) {
      throw new Error('org_uuid is required');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout

    const response = await postWithAuth(
      computeDailyAIAnalyticsEndpoint,
      payload,
      {
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response
        .text()
        .catch(() => 'Unable to read error');
      throw new Error(
        `Failed to compute daily AI analytics: ${response.status} ${errorText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    if (data.error) {
      throw new Error(`Server error: ${data.error}`);
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Compute daily AI analytics took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error computing daily AI analytics:', error);
    throw error;
  }
};

const computeDailyCoreAnalytics = async (
  payload: Omit<ComputeDailyAnalyticsRequest, 'integration'>
): Promise<ComputeDailyAnalyticsResponse> => {
  try {
    if (!payload.org_uuid) {
      throw new Error('org_uuid is required');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout

    const response = await postWithAuth(
      computeDailyCoreAnalyticsEndpoint,
      payload,
      {
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response
        .text()
        .catch(() => 'Unable to read error');
      throw new Error(
        `Failed to compute daily core analytics: ${response.status} ${errorText}`
      );
    }

    const data = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    if (data.error) {
      throw new Error(`Server error: ${data.error}`);
    }

    return data;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error(
        'Request timeout: Compute daily core analytics took too long to respond'
      );
      throw new Error('Request timeout - please try again');
    }
    console.error('Error computing daily core analytics:', error);
    throw error;
  }
};

// ── AI Usage Stats Table ──

const aiUsageStatsTableEndpoint = 'aiUsageStatsTable/';

export interface AIUsageStatsTableRequest {
  org_uuid: string;
  team_uuids?: string[];
  date_range: string;
  date_interval?: { start_date: string; end_date: string };
  view_type: 'individual' | 'team';
}

export interface AIUsageStatsMember {
  user_uuid: string;
  user_id: string;
  name: string;
  image: string | null;
  team_name: string;
  team_uuid: string;
  ai_tools_linked: string;
  cursor_loc: number;
  claude_loc: number;
  codex_loc: number;
  total_loc: number;
  cost_efficiency: number | null;
  acceptance_rate: number;
  ai_efficiency_rate: number;
  total_lines_added: number;
  total_lines_deleted: number;
  weekly_ai_loc: number[];
}

export interface AIUsageStatsTeam {
  team_uuid: string;
  team_name: string;
  total_members: number;
  ai_tools_used: string;
  total_cursor_loc: number;
  total_claude_loc: number;
  total_codex_loc: number;
  total_loc: number;
  avg_cost_efficiency: number;
  avg_acceptance_rate: number;
  avg_ai_efficiency_rate: number;
}

export interface AIUsageStatsTableResponse {
  view_type: string;
  members: AIUsageStatsMember[];
  teams: AIUsageStatsTeam[];
  total_members: number;
  total_teams: number;
  date_range: string;
  error?: string;
}

export const fetchAIUsageStatsTable = async (
  payload: AIUsageStatsTableRequest
): Promise<AIUsageStatsTableResponse> => {
  const response = await postWithAuth(
    apiUrl + aiUsageStatsTableEndpoint,
    payload
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch AI usage stats table: ${response.status}`);
  }

  const data = await response.json();
  return data;
};

// ── Claude Code Teams Plan (OTel) ──────────────────────────────────────

export interface TeamPlanUserRow {
  user_id: string;
  user_email: string;
  cost_usd_total: number;
  input_tokens_total: number;
  output_tokens_total: number;
  cache_read_tokens_total: number;
  api_request_count: number;
  session_count: number;
  active_time_s: number;
  lines_added: number;
  lines_removed: number;
  commits_count: number;
  prs_count: number;
  tool_accept_count: number;
  tool_reject_count: number;
  last_seen_at: string;
}

export interface TeamPlanDailyRow {
  date: string;
  cost_usd_total: number;
  input_tokens_total: number;
  output_tokens_total: number;
  cache_read_tokens_total: number;
  api_request_count: number;
  session_count: number;
  active_time_s: number;
  lines_added: number;
  lines_removed: number;
  commits_count: number;
  prs_count: number;
  tool_accept_count: number;
  tool_reject_count: number;
  active_users: number;
}

export interface TeamPlanUsageResponse {
  users: TeamPlanUserRow[];
  daily: TeamPlanDailyRow[];
  error?: string;
  truncated?: boolean;
}

const teamPlanUsageEndpoint = 'claudeCode/teamPlanUsage/';

export const fetchTeamPlanUsage = async (
  org_uuid: string,
  start_date: string,
  end_date: string
): Promise<TeamPlanUsageResponse> => {
  /** Fetch per-user Claude Code usage from OTel DynamoDB. */
  const response = await postWithAuth(apiUrl + teamPlanUsageEndpoint, {
    org_uuid,
    start_date,
    end_date,
  });
  if (!response.ok) throw new Error(`teamPlanUsage failed: ${response.status}`);
  return response.json();
};

export interface TeamPlanMemberTimelineResponse {
  user_id: string;
  user_email: string;
  days: TeamPlanDailyRow[];
  error?: string;
}

const teamPlanMemberTimelineEndpoint = 'claudeCode/teamPlanMemberTimeline/';

export const fetchTeamPlanMemberTimeline = async (
  org_uuid: string,
  user_id: string,
  start_date: string,
  end_date: string
): Promise<TeamPlanMemberTimelineResponse> => {
  /** Fetch per-day Claude Code usage for a single member from OTel DynamoDB. */
  const response = await postWithAuth(apiUrl + teamPlanMemberTimelineEndpoint, {
    org_uuid,
    user_id,
    start_date,
    end_date,
  });
  if (!response.ok)
    throw new Error(`teamPlanMemberTimeline failed: ${response.status}`);
  return response.json();
};

// --- Agent Insights (entelligence insights CLI) team rollup -----------------
// Behavioural/efficiency + spend metrics synced per developer by the CLI
// (`entelligence insights sync`), aggregated to the org/team for admins. This
// is the granular layer on top of the OTel volume/cost data above.

export interface AgentInsightsTeamMember {
  userUuid: string;
  name: string;
  email: string | null;
  // SCM handle (github/gitlab/bitbucket, roster-resolved); null when unknown.
  username?: string | null;
  role: string;
  image: string | null;
  sessions: number;
  // TASK-grain total. The team section is task-grain end to end so its
  // headline, facets, members and tokenSplit reconcile — keep using this for
  // anything that must add up against those.
  tokens: string;
  tokensRaw: number;
  // SESSION-grain total (ENT-4090): what this person actually consumed, the
  // same base RDS and Path B report. Task grain under-reports Cursor badly —
  // its billed total sits on the session root, leaving tasks partial — so a
  // member header read 266.8M against a true 1.367B. Use THIS for "how many
  // tokens did this person use"; use `tokens` only for task-grain arithmetic.
  // Optional: absent until the backend change ships.
  sessionTokens?: string;
  sessionTokensRaw?: number;
  // SESSION-grain session count (ENT-4582) — the count half of the same fix.
  // `sessions` is task grain and drops an agent whose parser left its tasks
  // partial, so a member header read 555 against a true 638 (Cursor's 81 and
  // Kiro's 2 absent). Use THIS for "how many sessions did this person run".
  // Optional: absent until the backend change ships.
  sessionCountRaw?: number;
  // This member's tokens as a share of the team total (0–100), computed
  // backend-side. Use this for the "Token Usage" column, not tokensRaw/max.
  tokenSharePct: number;
  // Cache-aware dollar spend for this developer. `cost` is display-formatted
  // ("$42"); `costRaw` is the exact number for sorting.
  cost: string;
  costRaw: number;
  costPerSession: string;
  // SESSION-grain spend: what this person actually cost, the same per-session
  // quantity the spend TILES sum. Task grain cannot see a task-less session at
  // all, and for externally billed Cursor sessions it sees nothing by design —
  // the whole bill lands on the session root as a remainder. Measured on a
  // customer org 2026-08-25: these rows summed to $3,479.78 against an
  // $8,112.86 org total, one member reading $17.39 against a true $447.58.
  // Use THIS for "what did this person spend"; use `cost` only for task-grain
  // arithmetic. `sessionCostPerSession` divides by the SESSION count, so the
  // headline and its sub-line stay on one grain.
  // Optional: absent until the backend change ships.
  sessionCost?: string;
  sessionCostRaw?: number;
  sessionCostPerSession?: string;
  efficiency: number;
  toolsPerTurn: number;
  // Failed-tool-call rate (%). Distinct from retryRate.
  errorRate: number;
  // error->retry-same-tool sequences as a share of tool calls (%). Backs the
  // "Retry Rate" column. Null = not computed on the serving path (backend
  // #4469 stopped fabricating 0.0 here) — render a dash, never "0%".
  retryRate: number | null;
  readsBeforeEdit: number;
  interrupts: number | null;
  lastActive: string | null;
  topSkills?: { name: string; count: number }[];
  // Busiest agent (display label, e.g. "Claude Code") and the full set of tools
  // this member has used — drives the Primary Tool column + "All AI Tools" filter.
  primaryAgent?: string | null;
  agents?: string[];
  // Weekly average efficiency for the sparkline + trend chart: the member's
  // last ~14 scored weeks, each with its Monday week-start date. Plotted on a
  // real time-scaled x-axis, so gaps read as longer segments, not even steps.
  // `sessions`/`tasks`/`tokens`/`dollars` are the same week's raw totals
  // (ENT-4931), letting the member-detail chart offer them as alternate
  // series and its hover tooltip show them together, without a second query.
  // Deliberately optional (not just the array — each field): a payload
  // served from a pre-ENT-4931 dashboard cache entry (PAYLOAD_VERSION < 9)
  // or an older backend still only sends `week`/`efficiency` (review finding
  // on #3759) — callers must not assume these are always present just
  // because `effTrend` exists.
  effTrend?: {
    week: string;
    // null = an active week with no efficiency-scored session (every
    // Cursor-only week: Cursor carries no per-turn tokens, so nothing scores).
    // The cost/tokens/sessions series are real for such a week; only the
    // efficiency series gaps. Never 0 — that would read as a real score.
    efficiency: number | null;
    sessions?: number;
    tasks?: number;
    tokens?: number;
    dollars?: number;
  }[];
  // Daily replacement for `effTrend` (ENT-4524): one point per UTC day over a
  // fixed 30-day window instead of a weekly fold over ~14 weeks. Additive
  // alongside `effTrend`, not a replacement of it -- a dashboard cache entry
  // computed before this shipped simply won't carry it yet, so callers must
  // fall back to `effTrend` when this is absent/too-short, the same "payload
  // shape can lag the backend" guard `effTrend` itself documents above.
  //
  // Two independently-sourced day-grain reads joined by (member, day, agent)
  // and NOT reconciled into one number (backend `_member_patterns_daily`):
  // `efficiency`/`tasks` come from session-activity facts, `sessions`/
  // `tokens`/`dollars`/`requests` come from the cost ledger. A day can
  // legitimately have ledger-billed sessions with no task-active session (or
  // the reverse) -- this is a real, known gap in the underlying data, not a
  // bug, so the two are never blended into a false-precision single figure.
  patternsDaily?: {
    day: string; // YYYY-MM-DD, UTC.
    // None when the day has no efficiency-scored session -- never 0, which
    // would read as a real (terrible) score.
    efficiency: number | null;
    sessions: number;
    tasks: number;
    tokens: number;
    dollars: number;
    requests: number;
    // Per-agent breakdown for the tooltip (V1 acceptance: "per-agent
    // requests, sessions, tasks, tokens, cost, basis"). `agent` is the RAW
    // backend key ("cursor" | "claude-code" | ...), not a display label --
    // map it with `PATTERNS_AGENT_LABEL` before rendering.
    by_agent: {
      agent: string;
      sessions: number;
      tasks: number;
      tokens: number;
      dollars: number;
      requests: number;
      cost_basis: string | null;
    }[];
  }[];
}

// One row of the member breakdown's "by Team" rollup — sessions joined to teams
// via UserModel.teamUUID; unassigned users bucket into "Not Assigned".
// `efficiency`/`errorRate`/`retryRate` are always null on the graph path
// (backend #4469 — unmeasured, not zero); render a dash, never "0%".
export interface AgentInsightsTeamRow {
  team: string;
  members: number;
  sessions: number;
  tokens: string;
  tokensRaw: number;
  cost: string;
  costRaw: number;
  efficiency: number | null;
  errorRate: number | null;
  retryRate: number | null;
  lastActive: string | null;
}

export interface AgentInsightsTeamTotals {
  sessions: number;
  tokens: string;
  tokensRaw: number;
  // Org-wide spend + the "Per Developer / Per Session / Per Dollar" cuts.
  // `costApprox` is true when some sessions predate the cache-token columns,
  // so the figure is an upper bound and the UI should flag it.
  cost: string;
  costRaw: number;
  costApprox: boolean;
  costPerSession: string;
  costPerDev: string;
  avgEfficiency: number;
  avgToolsPerTurn: number;
  avgErrorRate: number;
  readsBeforeEdit: number;
  interrupts: number | null;
  turns: number;
  tokenSplit: {
    prompt: string;
    completion: string;
    promptPct: number;
    total: string;
  };
}

export interface AgentInsightsTeamResponse {
  success: boolean;
  totals: AgentInsightsTeamTotals | Record<string, never>;
  members: AgentInsightsTeamMember[];
  byProject: {
    name: string;
    sessions: number;
    devs: number;
    tokens: string;
    tokensRaw: number;
    cost: string;
    costRaw: number;
  }[];
  byModel: {
    label: string;
    sessions: number;
    tokens: string;
    tokensRaw: number;
    cost: string;
    costRaw: number;
  }[];
  byAgent: {
    label: string;
    sessions: number;
    toolsPerTurn: number;
    efficiency: number;
    tokens: string;
    tokensRaw: number;
    cost: string;
    costRaw: number;
  }[];
  byTeam?: AgentInsightsTeamRow[];
  trend: {
    date: string | null;
    sessions: number;
    efficiency: number;
    tokensRaw: number;
    costRaw: number;
  }[];
  // Spend-over-time split for the Spend Trend "By Models" / "By Teams" tabs.
  // `series` lists the stacked-area keys (top-N by spend + "Other"); each point
  // is a wide row: { date, [seriesLabel]: cost }.
  trendByModel: {
    series: string[];
    points: { date: string; [series: string]: string | number }[];
  };
  trendByTeam: {
    series: string[];
    points: { date: string; [series: string]: string | number }[];
  };
  bySkill: { name: string; count: number }[];
  /** One row per MCP server, folded from the raw tool keys (`linear`, not
   *  `claude_ai_Linear`). `toolsUsed` is how many of the server's tools the
   *  team actually CALLED, not how many it exposes: the exposed count needs a
   *  live connection to the server and is not in the telemetry. */
  byMcpServer: {
    name: string;
    calls: number;
    toolsUsed: number;
    /** Engineers who have this server configured, over those whose config was
     *  captured at all. Null means not measured (no client has reported an MCP
     *  inventory yet), which must render as no reach line, not "0 of 0". */
    connectedEngineers: number | null;
    totalEngineers: number | null;
  }[];
  behaviour: {
    label: string;
    value: string;
    pct: number;
    higherBetter?: boolean;
  }[];
  wasteBreakdown: {
    items: { label: string; tokensRaw: number; tokens: string; pct: number }[];
    totalFmt: string;
    /** Daily stacked-trend series for the "Daily token waste by cause" card.
     *  `series` is the cause labels in the same order/spelling as `items` so
     *  cause↔colour stays in sync with the donut; each `points` row is wide:
     *  `{ date: "YYYY-MM-DD", [causeLabel]: tokens }`. Absent on a backend
     *  older than the trend change — the card then just doesn't render. */
    trend?: {
      series: string[];
      points: { date: string; [cause: string]: string | number }[];
    };
  };
  // Same nullability contract as MemberContextHealth: every gauge is
  // `number | null`, where null = unmeasured on the serving path (backend
  // #4469 stopped fabricating 0s) — exclude from scores, render a dash.
  contextHealth: {
    pressure: number | null;
    /** Null = unmeasured (the team read has no floor-drift source yet). */
    floorDrift: number | null;
    thrashPerSession: number | null;
    dupReadsPerSession: number | null;
    retriesPerSession: number | null;
  };
  membersNotSynced?: {
    userUuid: string | null;
    name: string;
    email: string | null;
    role: string;
    image: string | null;
    /** ISO timestamp of the last CLI-setup reminder emailed to this member, or
     * null if never reminded. Drives the per-row "reminded recently" hourglass
     * (24h cooldown) — same field the connection-status endpoint returns. */
    lastRemindedAt?: string | null;
  }[];
  membersTotal: number;
  membersActive: number;
  /** True when the org has Slack connected — the FE then offers per-member
   * "Remind" buttons (Slack DM) instead of plain status dots. */
  slackConnected?: boolean;
  /** Declared time window served (backend window-param PR; absent today). */
  window?: OverviewWindowField;
  error?: string;
  code?: string;
}

export interface AgentRemindResponse {
  success: boolean;
  sent: boolean;
  reason?: string;
  sentCount?: number;
  skippedCount?: number;
}

export const sendAgentInsightsReminder = async (
  userUuid: string
): Promise<AgentRemindResponse> => {
  const r = await postWithAuth(`${apiUrl}agentInsightsRemind/`, {
    user_uuid: userUuid,
  });
  if (!r.ok) throw new Error(`agentInsightsRemind failed: ${r.status}`);
  return r.json();
};

export const sendAgentInsightsBulkReminder =
  async (): Promise<AgentRemindResponse> => {
    const r = await postWithAuth(`${apiUrl}agentInsightsRemind/`, {
      all: true,
    });
    if (!r.ok) throw new Error(`agentInsightsRemind(all) failed: ${r.status}`);
    return r.json();
  };

/**
 * Time window a graph Overview endpoint declares it actually served. Arriving
 * with the backend window-param PR — absent on today's responses, so every
 * consumer must treat it as optional and fall back to the user's selection.
 * Typed loose (count | label | `{ days, label }`) so a backend shape choice
 * can't break the FE.
 */
export type OverviewWindowField =
  | number
  | string
  | { days?: number | null; label?: string | null }
  | null;

/** Query suffix for the graph Overview endpoints: `days=N` (omitted for All
 *  time) plus `team_uuid=<TeamUUID>` when an Owner scoped the page to one team.
 *  '' when neither applies. Endpoints that don't support a param ignore it. */
const overviewDaysQuery = (
  days?: number | null,
  teamUuid?: string | null
): string => {
  const qs = new URLSearchParams();
  if (days) qs.set('days', String(days));
  if (teamUuid) qs.set('team_uuid', teamUuid);
  const s = qs.toString();
  return s ? `?${s}` : '';
};

// --- Agent Insights per-engineer detail (admin member-detail modal) ---
// The member table row already carries the scorecard (efficiency, tokens, retry,
// sessions, tool mix, effTrend), so this endpoint returns only the *extras* the
// modal layers on top: peer percentiles, the activity heatmap, model split,
// per-agent breakdown, cached coaching, and the developer's recent sessions.

/** One coaching suggestion — metric-grounded, with a copy-paste fix. */
export interface AgentMemberCoachingSuggestion {
  title: string;
  severity: 'high' | 'medium' | 'low';
  evidence?: string;
  recommendation?: string;
  fix?: string;
  fix_type?: 'claude_md' | 'prompt' | 'workflow';
  difficulty?: 'quick' | 'moderate' | 'ambitious';
}

export interface AgentSessionIdentity {
  nativeSessionId?: string | null;
  nativeSessionTitle?: string | null;
  nativeTitleSource?: string | null;
  subagentIds?: string[] | null;
}

/** One of the developer's recent sessions (most recent 8). */
export interface AgentMemberSession {
  sessionId: string;
  sessionTitle?: string | null;
  agent: string;
  project: string | null;
  /** Canonical repositories observed on the session or its tasks. */
  repositories?: string[];
  /** "<project>/<workspace>" when the session ran in a Conductor worktree. */
  conductorWorkspace?: string | null;
  model: string | null;
  startTime: string | null;
  /** Latest observed task activity; absent on legacy/session-only rows. */
  lastWorkedAt?: string | null;
  taskCount?: number | null;
  sessionIdentity?: AgentSessionIdentity | null;
  tokens: string;
  cost: string;
  efficiency: number | null;
  /**
   * Why a Cursor session has no billed usage — `no_token`, `api_error`,
   * `no_events`, `unmatched`, `prose_log_source`, `no_window`, `billed_zero`,
   * or `ok`. Absent/null for other agents and for pushes from CLIs older
   * than 0.1.219. Render the reason instead of a `$0` that reads as free.
   */
  usageState?: string | null;
  /**
   * Why a session with no `project` has no repo attributed — `no_signal`,
   * `no_file_activity`, `system_noise` (none of these are developer file
   * work, so render "Non-repo work"), or `project_unlinked` (file activity
   * exists but couldn't be mapped to a project, so render "attribution
   * pending"). Null/absent when `project` is already set, or when the
   * session predates classification — treat the latter as pending too.
   */
  activityClass?: string | null;
}

/** Developer capability/tool-usage maturity score. */
export interface MemberMaturity {
  score: number;
  band: string;
  capabilities: { key: string; label: string; used: boolean }[];
}

/** One category of wasted tokens/cost (compaction, repeated edits, etc). */
export interface MemberWasteItem {
  key: string;
  label: string;
  tokens: string;
  tokensRaw: number;
  cost: string;
  costRaw: number;
}

/** Context-window health signals (avg per session). */
export interface MemberContextHealth {
  pressure: number | null;
  floorDrift: number | null;
  dupReadsPerSession: number | null;
  thrashPerSession: number | null;
  tokensPerUsefulEdit: number | null;
  subagentRate: number | null;
  retriesPerSession: number | null;
}

/** Read/edit/research behavior ratios. */
export interface MemberBehavior {
  readEditRatio: number | null;
  researchMutationRatio: number | null;
  writeVsEditPct: number | null;
  readsBeforeEdit: number | null;
}

export interface HighlightTask {
  name: string | null;
  category: string | null;
  outcome: string | null;
  usd: number;
}

export interface SessionHighlight {
  sessionId: string | null;
  agentSessionId?: string | null;
  date: string;
  repo: string | null;
  agent: string | null;
  cost: number;
  shippedUsd: number;
  failedUsd: number;
  wasteUsd: number | null;
  wastePct: number;
  score: number;
  taskCount: number;
  topTasks: HighlightTask[];
  note: string[] | string | null;
}

export interface SessionHighlights {
  // 30 for the default window; null when the page's ?days= selector applied.
  windowDays: number | null;
  since?: string;
  best: SessionHighlight | null;
  worst: SessionHighlight | null;
}

export interface TeamSkillRecommendation {
  // Normalized skill name (e.g. "entloop") — the slash-command the dev runs.
  skill: string;
  // One-line description from the org's authored skill definition.
  description?: string;
  // Adoption phrase from backend, e.g. "6 of 10 teammates" (NOT a number).
  teamAdoption?: string;
  why?: string;
  how?: string;
}

/** One duration bucket ("<1m", "1-5m", ...) and how many of the member's
 * sessions landed in it. Fixed bucket set, server-defined order — render in
 * the order given, don't re-sort. */
export interface DurationHistogramBucket {
  label: string;
  count: number;
}

/** Session-length distribution (member) plus the org's mean/median (team) for
 * a reference-line overlay. Graph-only: `null` on the RDS fallback path —
 * the section hides rather than showing an empty chart. */
export interface MemberDurationHistogram {
  buckets: DurationHistogramBucket[];
  teamMeanSeconds: number | null;
  teamMedianSeconds: number | null;
  // Which bucket label the team figure falls in, precomputed server-side
  // against the SAME boundaries `buckets` uses — position the reference
  // line at this bucket, never re-derive it from the seconds value.
  teamMeanBucketLabel: string | null;
  teamMedianBucketLabel: string | null;
}

/** Task-grain repository rollup for the member Work Explorer. Session-root
 * billing remainders are intentionally excluded because they have no truthful
 * repository attribution. */
export interface AgentInsightsMemberRepositoryIndex {
  basis: 'task_attributed';
  /** Exact session-clock bounds used to build the repository rollup. */
  window?: {
    since: string | null;
    until: string | null;
  };
  repositories: {
    repository: string;
    taskCount: number;
    /** Distinct only within this repository; not additive across rows. */
    sessionCount: number;
    tokens: number;
    taskCost: number;
    lastWorkedAt: string | null;
  }[];
}

/** Task-day scoped member work, carried with the cached member-detail response.
 * This is intentionally not a replacement for the complete session ledger:
 * coverage states how much of the bill has durable task attribution. */
export interface AgentInsightsMemberWorkDimensions {
  windowStart: string | null;
  windowEnd: string | null;
  timezone: 'UTC';
  snapshotAt: string | null;
  /**
   * Additive WI-14 contract. Its absence means this cache entry only has
   * window rollups, which cannot truthfully scope the daily Patterns chart.
   */
  seriesVersion?: 1;
  efficiencyCoverage?: {
    state: 'unavailable';
    reason: string;
  };
  sessionCountBasis?: 'explicit_root_or_source_session';
  coverage: {
    state: 'complete' | 'partial' | 'inconsistent' | 'unavailable';
    scoped: { requests: number; tokens: number; cost: number };
    expected: { requests: number; tokens: number; cost: number };
    unallocated?: { requests: number; tokens: number; cost: number };
  };
  repositories: {
    repository: string;
    lastWorkedAt: string | null;
    sessions: number;
    tasks: number;
    agents: string[];
    requests: number;
    totalTokens: number;
    cost: number;
    modelsComplete: boolean;
    models: { model: string; requests: number; tokens: number }[];
    /** Sparse UTC daily points from exact task-attributed billing facts. */
    daily?: AgentInsightsMemberScopedPatternPoint[];
  }[];
  tasks: {
    taskId: string | null;
    name: string;
    repository: string;
    lastWorkedAt: string | null;
    sessions: number;
    tasks: number;
    agents: string[];
    requests: number;
    totalTokens: number;
    cost: number;
    modelsComplete: boolean;
    models: { model: string; requests: number; tokens: number }[];
    /** Sparse UTC daily points from exact task-attributed billing facts. */
    daily?: AgentInsightsMemberScopedPatternPoint[];
  }[];
  /**
   * Source-grain details for a selected UTC day. These are intentionally
   * separate from the chart point so the browser never substitutes an
   * unscoped day-drill-down after a repository or task is selected.
   */
  days?: AgentInsightsMemberScopedPatternDay[];
}

export interface AgentInsightsMemberScopedPatternPoint {
  day: string;
  sessions: number;
  tasks: number;
  requests: number;
  totalTokens: number;
  cost: number;
  /** Task-day efficiency is not measured; clients must not synthesize it. */
  efficiency: null;
  modelsComplete: boolean;
  models: { model: string; requests: number; tokens: number }[];
}

export interface AgentInsightsMemberScopedPatternDay extends AgentInsightsMemberScopedPatternPoint {
  coverage: AgentInsightsMemberWorkDimensions['coverage'];
  rows: (AgentInsightsMemberScopedPatternPoint & {
    taskId: string;
    name: string;
    repository: string;
    sessionId: string | null;
    rootSessionId: string | null;
    agentSessionId: string | null;
    agent: string;
    costBasis: string;
  })[];
}

export interface AgentInsightsMemberDetailResponse {
  success: boolean;
  // False when the developer has no synced sessions yet.
  found: boolean;
  // Day-grain Patterns series bounded by the request's `days` window (ENT-4544
  // follow-up), same point shape as the team row's `patternsDaily`. Absent on a
  // cache entry written before the backend emitted it; the member page then
  // falls back to the row's own series.
  patternsDaily?: AgentInsightsTeamMember['patternsDaily'] | null;
  // Best / threshold-gated worst session over the last 30 days. Additive
  // bonus block: null or absent hides the whole section. `worst` is null
  // for a clean month by design (never rank-based).
  sessionHighlights?: SessionHighlights | null;
  // Session-length histogram + team mean/median overlay. Same additive-block
  // contract as `sessionHighlights`: null hides the section, key always
  // present.
  durationHistogram?: MemberDurationHistogram | null;
  peer?: {
    // Share of peers this developer scores above on efficiency (0–100).
    efficiencyPercentile: number | null;
    // "Top N%" by total token volume (1–100), clamped so #1 reads Top 1%.
    volumeTopPercent: number | null;
  };
  /** ENT-4702 — sync lifecycle: when this member's CLI started, and how much of
   *  their history was back-filled off disk after install.
   *
   *  The contribution heatmap reads as a wall of gaps without this. Most of
   *  those gaps are days BEFORE the CLI existed on that machine, whose sessions
   *  were captured retroactively — back-fill, not missed syncs. Optional: a
   *  backend that predates the field omits it and the card is not rendered,
   *  rather than showing a split we cannot compute. */
  syncProfile?: {
    /** ISO. The CLI's API key mint — the only durable install record we hold.
     *  Null when the member has no key on record, in which case the
     *  historical/live split is unknowable and everything counts as live. */
    cliSignupAt: string | null;
    firstSessionAt: string | null;
    lastSessionAt: string | null;
    sessionsTotal: number;
    /** Activity predating `cliSignupAt`: pulled off disk AFTER install. */
    sessionsHistorical: number;
    sessionsLive: number;
    activeDaysAllTime: number;
  } | null;
  activity?: {
    peakHourUTC: number | null;
    peakHourLabel: string | null;
    longestStreakDays: number;
    activeDays: number;
    deepestSessionTurns: number;
    hourHistogram: number[];
    clock: { dow: number; hour: number; count: number }[];
    // Sparse per-(UTC date, UTC hour) session counts — the month-calendar
    // heatmap's source (ENT-4373). Kept at hour grain on purpose: the calendar
    // day a session belongs to depends on the viewer's timezone, so the
    // browser does the bucketing. Optional: a backend that predates the field
    // omits it, and the card falls back to the server's UTC scalars.
    dayHours?: {
      date: string;
      hour: number;
      count: number;
      /** Complete qualified work-session IDs, for local-day deduplication. */
      workSessionIds?: string[];
      /** Theme names touched in this bucket; absent when none. */
      themes?: { name: string; count: number }[];
    }[];
    tokenTrend: { date: string; tokens: number }[];
    // Last-7-days sessions/tasks/tokens/cost, one point per calendar day,
    // zero-filled (ENT-4931 v2). Optional, unlike `tokenTrend`: only the
    // graph path computes it — a member served from the RDS fallback (graph
    // outage, or not yet in the graph) has `activity` without this key.
    dailyTrend?: {
      date: string;
      sessions: number;
      tasks: number;
      tokens: number;
      dollars: number;
    }[];
  };
  models?: {
    label: string;
    pct: number;
    tokens: string;
    tokensRaw: number;
    cost: string;
    costRaw: number;
  }[];
  /** Optional WI-08 model snapshot. Older member-detail responses keep using
   * `models` above; the Patterns Models mode labels dated series as unavailable
   * until this additive block is present. */
  modelUsage?: AgentInsightsMemberModelUsage | null;
  repositoryIndex?: AgentInsightsMemberRepositoryIndex | null;
  workDimensions?: AgentInsightsMemberWorkDimensions | null;
  /**
   * Fixed trailing-30-day task-attributed ledger. This is deliberately
   * separate from page-window `workDimensions`: the Tasks tab can perform
   * local search, sort and pagination only when this bounded source is
   * available.
   */
  taskLedger?: AgentInsightsMemberTaskLedger | null;
  perAgent?: {
    agent: string;
    sessions: number;
    efficiency: number;
    toolsPerTurn: number;
    tokens: string;
    // Cost-ledger columns (ENT-4526), read over the SAME window as the
    // fields above but from a SEPARATE source (the cost ledger, not
    // session-facts) and never reconciled with them: an agent billed with no
    // session-facts row (or vice versa) still gets a row, so `sessions: 0`
    // can appear alongside real `requests`/`cost`. Optional, unlike the
    // fields above: absent on a payload served from a pre-ENT-4526 backend.
    requests?: number;
    cost?: string;
    costRaw?: number;
    // "vendor" | "rate card" | "mixed", or null when the agent has no
    // ledger-billed activity in this window at all (not "free" -- just
    // nothing to attribute a basis to).
    pricedBy?: string | null;
  }[];
  coaching?: {
    headline?: string;
    persona?: string;
    suggestions?: AgentMemberCoachingSuggestion[];
    wins?: string[];
  } | null;
  teamSkillRecommendations?: TeamSkillRecommendation[];
  // Trace-mined skills this member repeatedly steers the agent through (recurring
  // high-effort work). Same shape as the Overview recommendations card.
  personalSkills?: SkillRecommendation[];
  // Every skill this member invoked in the page window, most-used first
  // (ENT-4827). The members-list row's `topSkills` stays capped at 5. Absent
  // or null = unavailable (older backend, cache row, failed read), never
  // "no skills": the card keeps the top 5.
  skillsUsed?: { name: string; count: number }[] | null;
  sessions?: AgentMemberSession[];
  maturity?: MemberMaturity | null;
  waste?: MemberWasteItem[];
  // Waste this member could recover, priced at the rate the wasted tokens
  // actually bill at (cache-read) and bounded per session by that session's own
  // cost, so it is always a SUBSET of spend. `ofSpendRaw` is the spend it is a
  // subset of — same sessions, same window as `costRaw` — so the UI can show
  // the share instead of leaving two unrelated-looking dollar figures side by
  // side. Optional: an older backend omits it.
  reclaimable?: {
    tokens: string;
    tokensRaw: number;
    cost: string;
    costRaw: number;
    ofSpendRaw?: number;
    // Token-grain mirror of `ofSpendRaw`: the billed-token base (cache-
    // inclusive) that `tokensRaw` is a share of, over the same sessions and
    // window. Lets the capacity-metered tile state its share the way the
    // spend tile does. Optional: an older backend omits it.
    ofTokensRaw?: number;
  };
  contextHealth?: MemberContextHealth | null;
  behavior?: MemberBehavior | null;
  error?: string;
  code?: string;
}

export interface AgentInsightsMemberModelTokenTotals {
  /** Recorded billed-day cost, on the same window/model as tokens. Null = unavailable.
   * Requires complete pricing provenance; not a reconciled invoice. */
  costRaw?: number | null;
  totalTokens?: number;
  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
}

export interface AgentInsightsMemberModelDay
  extends AgentInsightsMemberModelTokenTotals {
  day: string;
}

export interface AgentInsightsMemberModelRow {
  /** Empty, `default`, and unknown model ids render as `Unknown`. */
  model: string | null;
  pct?: number | null;
  totals?: AgentInsightsMemberModelTokenTotals;
  daily?: AgentInsightsMemberModelDay[];
}

export interface AgentInsightsMemberModelCoverage {
  state: 'complete' | 'partial' | 'unavailable';
  observedRequests?: number | null;
  expectedRequests?: number | null;
}

export interface AgentInsightsMemberModelUsage {
  windowKind?: 'bounded' | 'all_time';
  windowStart?: string | null;
  windowEnd?: string | null;
  timezone?: string | null;
  snapshotAt?: string | null;
  models?: AgentInsightsMemberModelRow[];
  coverage?: AgentInsightsMemberModelCoverage | null;
}

export interface AgentInsightsMemberTaskLedgerCoverage {
  state: 'complete' | 'partial' | 'unavailable';
  scoped?: { requests?: number; tokens?: number; cost?: number } | null;
  expected?: { requests?: number; tokens?: number; cost?: number } | null;
}

export interface AgentInsightsMemberTaskLedgerTask {
  taskId: string | null;
  name: string;
  repository: string | null;
  lastWorkedAt: string | null;
  sessions: number;
  agents: string[];
  totalTokens: number;
  cost: number;
  modelsComplete: boolean;
  models: { model: string; tokens: number }[];
}

export interface AgentInsightsMemberTaskLedger {
  windowStart?: string | null;
  windowEnd?: string | null;
  timezone?: string | null;
  snapshotAt?: string | null;
  coverage?: AgentInsightsMemberTaskLedgerCoverage | null;
  tasks?: AgentInsightsMemberTaskLedgerTask[];
}

const agentInsightsMemberDetailEndpoint = 'agentInsightsMemberDetail/';

export const fetchAgentInsightsMemberDetail = async (
  org_uuid: string,
  user_uuid: string,
  days?: number | null
): Promise<AgentInsightsMemberDetailResponse> => {
  /** Fetch one engineer's admin drill-down (peer/activity/coaching/sessions).
   * `days` applies the same session-clock window to graph-backed member-detail
   * blocks, including model usage, costs, and the repository rollup, so the
   * page stays aligned with the Overview leaderboard; omitted = all-time. */
  const query = days != null ? `?days=${days}` : '';
  const response = await postWithAuth(
    apiUrl + agentInsightsMemberDetailEndpoint + query,
    {
      org_uuid,
      user_uuid,
    }
  );
  if (!response.ok)
    throw new Error(`agentInsightsMemberDetail failed: ${response.status}`);
  return response.json();
};

// --- Agent Insights member brief (ENT-4198 nightly worker / ENT-4304 read) ---
// Work themes, anomalies worth a look, and Ellie-ready suggested prompts,
// computed nightly and served from a cache-only read (no live-compute
// fallback, so a member added since the last nightly run gets the empty
// shape below, not an error).

export interface AgentInsightsMemberBriefTheme {
  name: string;
  summary: string;
  tasks: number;
  sessions: number;
  spend: string;
  spendRaw: number;
  lastActive: string | null;
}

export interface AgentInsightsMemberBriefAnomaly {
  question: string;
  evidence: string;
}

export interface AgentInsightsMemberBriefResponse {
  success: boolean;
  themes: AgentInsightsMemberBriefTheme[];
  anomalies: AgentInsightsMemberBriefAnomaly[];
  suggestedPrompts: string[];
  model: string | null;
  generatedAt: string | null;
  error?: string;
  code?: string;
}

const agentInsightsMemberBriefEndpoint = 'agentInsights/member/brief/';

export const fetchAgentInsightsMemberBrief = async (
  org_uuid: string,
  user_uuid: string
): Promise<AgentInsightsMemberBriefResponse> => {
  /** Fetch one engineer's nightly Member Brief (ENT-4198/ENT-4304): work
   * themes, anomalies worth a look, and suggested prompts. Self-view is
   * always allowed; an admin may pass a teammate's user_uuid. */
  const response = await postWithAuth(
    apiUrl + agentInsightsMemberBriefEndpoint,
    {
      org_uuid,
      user_uuid,
    }
  );
  if (!response.ok)
    throw new Error(`agentInsightsMemberBrief failed: ${response.status}`);
  return response.json();
};

// --- Agent Insights member sync health (ENT-4197 ledger / ENT-4304 member page) ---
// Per-day sync-gap counts plus current CLI version vs this org's floor.
// Admin-only (support tool, not per-member self-service), so the fetch
// soft-fails to the empty shape on any non-2xx rather than throwing —
// matching engineerCostCaps.ts's convention for a viewer-gated read.

export interface AgentInsightsMemberSyncHealthSession {
  session_id: string;
  agent: string;
  created_at: string;
  status: string;
}

export interface AgentInsightsMemberSyncHealthDay {
  date: string;
  created: number;
  processed: number;
  store_divergence: number;
  missing: number;
  envelope_only: number;
  partial_envelope: number;
  failed_in_service: number;
  read_error: number;
  sessions: AgentInsightsMemberSyncHealthSession[];
}

export interface AgentInsightsMemberSyncHealthResponse {
  success: boolean;
  days: AgentInsightsMemberSyncHealthDay[];
  no_inventory_dates: string[];
  cliVersion: string | null;
  lastHeartbeatAt: string | null;
  scheduleState: string | null;
  minCliVersion: string | null;
  meetsFloor: boolean;
  error?: string;
  code?: string;
}

const agentInsightsMemberSyncHealthEndpoint =
  'agentInsights/member/sync-health/';

const EMPTY_MEMBER_SYNC_HEALTH_RESPONSE: AgentInsightsMemberSyncHealthResponse =
  {
    success: true,
    days: [],
    no_inventory_dates: [],
    cliVersion: null,
    lastHeartbeatAt: null,
    scheduleState: null,
    minCliVersion: null,
    meetsFloor: true,
  };

export const fetchAgentInsightsMemberSyncHealth = async (
  user_uuid: string,
  start_date: string,
  end_date: string
): Promise<AgentInsightsMemberSyncHealthResponse> => {
  /** Fetch one developer's sync-health ledger. Admin-only server-side; never
   * throws, so a plain reader (the raw ledger table) can just render the
   * empty shape either way. But a genuine failure (5xx, network, malformed
   * body) is returned with `success: false`, DISTINCT from a well-formed
   * "nothing to report" response -- a reader making a health claim from
   * this (the Data Health card's "no gaps" state) must check `success`
   * first, or a real outage renders as a false-positive "all clear"
   * (entloop-caught, MAJOR). */
  try {
    const qs = new URLSearchParams({ user_uuid, start_date, end_date });
    const response = await getWithAuth(
      `${apiUrl}${agentInsightsMemberSyncHealthEndpoint}?${qs.toString()}`
    );
    if (!response.ok) {
      return { ...EMPTY_MEMBER_SYNC_HEALTH_RESPONSE, success: false };
    }
    const data =
      (await response.json()) as Partial<AgentInsightsMemberSyncHealthResponse>;
    // A success:true body missing/mistyping any field the Data Health
    // card's CLI-recovery derivation reads must fail closed, not pass
    // through with an implicit default -- a missing `meetsFloor` would
    // otherwise read as falsy and wrongly render "Upgrade the CLI"
    // (entloop-caught, MAJOR).
    if (
      !data ||
      data.success !== true ||
      !Array.isArray(data.days) ||
      !Array.isArray(data.no_inventory_dates) ||
      typeof data.meetsFloor !== 'boolean' ||
      (data.cliVersion !== null && typeof data.cliVersion !== 'string') ||
      (data.lastHeartbeatAt !== null &&
        typeof data.lastHeartbeatAt !== 'string') ||
      (data.scheduleState !== null && typeof data.scheduleState !== 'string') ||
      (data.minCliVersion !== null && typeof data.minCliVersion !== 'string')
    ) {
      return { ...EMPTY_MEMBER_SYNC_HEALTH_RESPONSE, success: false };
    }
    return data as AgentInsightsMemberSyncHealthResponse;
  } catch (error) {
    console.error('Error fetching member sync health:', error);
    return { ...EMPTY_MEMBER_SYNC_HEALTH_RESPONSE, success: false };
  }
};

// --- Agent Insights member reprocess (ENT-4197 "Re-sync missing window") ---
// Two-step: preview (read-only, GET) shows the blast radius, then fire
// (POST) actually queues the directive. A `success: false` body is a real,
// EXPECTED business outcome here (most commonly the backend's below-floor
// refusal) -- it is returned to the caller to branch on, not thrown, so its
// own humane `error` text (already written for direct display, matching
// the invalid_input convention) can reach the UI without a generic wrapper
// hiding it. Only a call that couldn't even produce a typed response at all
// (network failure, malformed body) throws, and that thrown message must
// never itself reach the UI raw -- callers show a fixed generic message and
// log the real one (entloop-caught, MAJOR). Session-id-level targeting (the
// more granular per-session drill-down in the ticket's UX mock) is out of
// scope here; only the date-window form is used by the Data Health card's
// gap re-sync button.

export interface MemberReprocessPreviewResponse {
  success: boolean;
  affected_session_count: number;
  error?: string;
  code?: string;
}

const agentInsightsMemberReprocessPreviewEndpoint =
  'agentInsights/member/reprocess-preview/';

export const fetchAgentInsightsMemberReprocessPreview = async (
  user_uuid: string,
  resume_from: string,
  resume_to: string
): Promise<MemberReprocessPreviewResponse> => {
  /** Blast-radius preview for a would-be date-window reprocess directive
   * (read-only -- fires nothing). */
  const qs = new URLSearchParams({ user_uuid, resume_from, resume_to });
  const response = await getWithAuth(
    `${apiUrl}${agentInsightsMemberReprocessPreviewEndpoint}?${qs.toString()}`
  );
  const data =
    (await response.json()) as Partial<MemberReprocessPreviewResponse> | null;
  // A non-2xx status claiming success:true is internally contradictory --
  // never trust it, even though a non-2xx CAN legitimately carry a real
  // success:false business refusal (entloop-caught, MAJOR). A success:true
  // body missing affected_session_count is equally untrustworthy: the
  // confirm step reads that count directly, and silently passing through
  // `undefined` would leave the button stuck instead of surfacing an error.
  if (
    !data ||
    typeof data.success !== 'boolean' ||
    (!response.ok && data.success) ||
    (data.success && !Number.isFinite(data.affected_session_count))
  ) {
    throw new Error(
      `agentInsightsMemberReprocessPreview failed: ${response.status}`
    );
  }
  return data as MemberReprocessPreviewResponse;
};

export interface MemberReprocessResponse {
  success: boolean;
  org_uuid?: string;
  user_uuid?: string;
  resume_generation?: number;
  resume_from?: string | null;
  resume_to?: string | null;
  session_ids?: string[] | null;
  error?: string;
  code?: string;
}

const agentInsightsMemberReprocessEndpoint = 'agentInsights/member/reprocess/';

export const fetchAgentInsightsMemberReprocess = async (
  user_uuid: string,
  resume_from: string,
  resume_to: string,
  reason?: string
): Promise<MemberReprocessResponse> => {
  /** Actually queue the per-client reprocess directive for [resume_from,
   * resume_to]. The backend refuses (typed invalid_input, human-readable
   * `error`) when the member's heartbeat CLI is below this org's version
   * floor -- a directive an outdated CLI can't obey would sit unconsumed
   * and burn the one-shot-per-generation semantics. */
  const response = await postWithAuth(
    apiUrl + agentInsightsMemberReprocessEndpoint,
    {
      user_uuid,
      resume_from,
      resume_to,
      reason,
    }
  );
  const data =
    (await response.json()) as Partial<MemberReprocessResponse> | null;
  // Same contradictory-response guard as the preview call above: a non-2xx
  // status claiming success:true is never trustworthy (entloop-caught,
  // MAJOR). Unlike preview, no field of a success payload is read by any
  // caller, so no further shape validation is needed here.
  if (
    !data ||
    typeof data.success !== 'boolean' ||
    (!response.ok && data.success)
  ) {
    throw new Error(`agentInsightsMemberReprocess failed: ${response.status}`);
  }
  return data as MemberReprocessResponse;
};

// --- Agent Insights onboarding (slim CLI connection status + completion latch) ---
// The lightweight counterpart of `agentInsightsTeam/` — only connection state,
// no spend/efficiency rollup, so the onboarding wizard loads fast. Org is taken
// from the authenticated admin; the optional `team_uuids` query scopes members.

/** Sync-status meter verdict — server-computed, see `agent_insights/specs/06-apis.md`. */
// 'not_syncing' (ENT-4931) means the member's CLI heartbeat has gone stale
// (>= HEARTBEAT_STALE_DAYS, backend controller.py) — a machine-liveness
// signal, distinct from 'stuck' (machine alive, segmentation backlog not
// moving). It overrides even a high `syncPercent`: a percent computed from
// sessions that landed before the machine went quiet says nothing about
// whether it's still syncing now.
export type AgentInsightsSyncStatus =
  | 'healthy'
  | 'progressing'
  | 'stuck'
  | 'not_syncing';

export interface AgentInsightsTeamStatusMember {
  userUuid: string | null;
  name: string;
  /** False when `name` is only an anonymized VCS login (a hex hash from an
   *  identity provider that strips names), so the row must not present it as a
   *  name. Absent on older payloads, which always carried a real name. */
  nameResolved?: boolean;
  email: string | null;
  role: string;
  image: string | null;
  lastActive?: string | null;
  lastRemindedAt?: string | null;
  /** Heartbeat-sourced CLI version. Connected members only; null if never heartbeated. */
  cliVersion?: string | null;
  /** Which CLI sent the heartbeat: `'ellie'`, or null for the Python CLI
   *  (`entelligence-cli`). The two number their releases independently, so
   *  `cliVersion` is only comparable between members on the same CLI. */
  clientKind?: string | null;
  /** 0-100, % of this member's sessions that ARRIVED carrying usable metrics
   *  (tokens or turns) — the metrics lane. Connected members only.
   *
   *  NOT the same question as `analyzedPercent` below, and the two fail
   *  independently (ENT-4702). A member on an old CLI that cannot segment sits
   *  at 100 here and 0 there: every session is on the dashboards with its cost,
   *  model and activity, none has a task breakdown. Reporting that member as
   *  "0% synced" described data we hold as data we had lost. */
  syncPercent?: number | null;
  /** 0-100, the subset of the same denominator that segmentation also cut into
   *  tasks. Absent on responses from a backend older than ENT-4702, so treat
   *  `undefined` as "not reported" and render nothing rather than 0%. */
  analyzedPercent?: number | null;
  /** ENT-4702 — real session counts at two windows, server-computed.
   *
   *  Exists because the client cannot honestly derive them: multiplying a
   *  member's all-time session total by their 7-day sync percent mixes grains
   *  and counts nothing. Absent on a pre-ENT-4702 backend, in which case the
   *  readiness strip reports nothing rather than an estimate. */
  sessionCounts?: {
    /** Window `window` covers. 30 today; read it rather than assuming. */
    windowDays: number;
    window: { total: number; synced: number; analyzed: number };
    allTime: { total: number; synced: number; analyzed: number };
  } | null;
  syncStatus?: AgentInsightsSyncStatus | null;
  /** ISO timestamp of the most recent session to finish segmentation. */
  lastProgressAt?: string | null;
  /** ISO timestamp of this member's last CLI heartbeat, whatever their
   *  syncStatus — surfaced so a 'not_syncing' row can show how long the
   *  machine has been quiet, not just that it has. */
  lastHeartbeatAt?: string | null;
}

export interface AgentInsightsTeamStatusResponse {
  success: boolean;
  members: AgentInsightsTeamStatusMember[];
  membersNotSynced: AgentInsightsTeamStatusMember[];
  membersTotal: number;
  membersActive: number;
  slackConnected?: boolean;
  error?: string;
  code?: string;
}

export const fetchAgentInsightsTeamStatus = async (
  teamUuids: string[] = []
): Promise<AgentInsightsTeamStatusResponse> => {
  const q = teamUuids.length
    ? `?team_uuids=${encodeURIComponent(teamUuids.join(','))}`
    : '';
  const r = await getWithAuth(`${apiUrl}agentInsightsTeamStatus/${q}`);
  if (!r.ok) throw new Error(`agentInsightsTeamStatus failed: ${r.status}`);
  return r.json();
};

// --- Agent Insights usage breakdown (Usage Across card) --------------------
// Token share by model + tool, filterable by team/project. Returns the team +
// project lists that populate the filter dropdowns.
export interface AgentInsightsUsageRow {
  label: string;
  tokensRaw: number;
  tokens: string;
  sessions: number;
}

export interface AgentInsightsUsageResponse {
  success: boolean;
  teams: { uuid: string; name: string }[];
  projects: string[];
  byModel: AgentInsightsUsageRow[];
  byAgent: AgentInsightsUsageRow[];
}

// --- Usage Across card: "By Person" tab -------------------------------
// One member's usage split by model — a stacked-bar row, `pct` already
// expressed as a share of THAT member's own tokens (segments sum to 100),
// not of the org total (`byModel`/`byAgent` above are org-wide shares).
export interface AgentInsightsPersonModelRow {
  label: string;
  tokensRaw: number;
  pct: number;
}

export interface AgentInsightsPersonUsageRow {
  userUuid: string;
  name: string;
  image: string | null;
  tokensRaw: number;
  tokens: string;
  byModel: AgentInsightsPersonModelRow[];
}

export interface AgentInsightsPeopleModelsResponse {
  people: AgentInsightsPersonUsageRow[];
}

export const fetchAgentInsightsOnboardingStatus = async (): Promise<{
  success: boolean;
  completed: boolean;
}> => {
  const r = await getWithAuth(`${apiUrl}agentInsightsOnboardingStatus/`);
  if (!r.ok)
    throw new Error(`agentInsightsOnboardingStatus failed: ${r.status}`);
  return r.json();
};

export interface AgentInsightsDataStatusResponse {
  success: boolean;
  /** True when the org has transcript-grade (graph-served) sessions. */
  hasTranscriptData: boolean;
  /** Sessions the dashboards will actually show (lane-excluded graph). */
  servedSessions: number;
  /** Metrics-only rows recorded for the org — the connect page's teaser. */
  telemetrySessions: number;
  /** Org's most recent CLI push (ISO), the header's "last indexed" signal. */
  lastSyncedAt?: string | null;
}

export const fetchAgentInsightsDataStatus =
  async (): Promise<AgentInsightsDataStatusResponse> => {
    const r = await getWithAuth(`${apiUrl}agentInsights/data-status/`);
    if (!r.ok) throw new Error(`agentInsights/data-status failed: ${r.status}`);
    return r.json();
  };

export const completeAgentInsightsOnboarding = async (): Promise<{
  success: boolean;
  completed: boolean;
}> => {
  const r = await postWithAuth(`${apiUrl}agentInsightsCompleteOnboarding/`, {});
  if (!r.ok)
    throw new Error(`agentInsightsCompleteOnboarding failed: ${r.status}`);
  return r.json();
};

/**
 * Record a partner promo redemption (attribution) against the current org —
 * e.g. "this org came in through Fireworks with code X".
 *
 * ponytail: attribution is non-critical to the prospect's flow — a logging
 * hiccup (or the backend endpoint not existing yet) must never block onboarding.
 * So this NEVER throws: on any HTTP failure it logs and returns
 * `unreachable: true`, and the redeem step waves the prospect through.
 *
 * `unreachable` is what separates "we couldn't ask" from "the backend said no":
 * the promo step has no Skip button, so a 500 must not read as "bad code" and
 * strand someone behind a mandatory gate.
 *
 * Returns:
 *   `{ recorded, valid, partner, unreachable }` — `valid`/`partner` come from
 *   the backend and are only meaningful when `unreachable` is false.
 */
export const redeemAgentInsightsPromo = async (
  code: string
): Promise<{
  recorded: boolean;
  valid: boolean;
  partner: string | null;
  unreachable: boolean;
}> => {
  try {
    const r = await postWithAuth(`${apiUrl}agentInsightsRedeemPromo/`, {
      code,
    });
    if (!r.ok) {
      console.error(`agentInsightsRedeemPromo failed: ${r.status}`);
      return {
        recorded: false,
        valid: false,
        partner: null,
        unreachable: true,
      };
    }
    // `await` inside the try on purpose: a non-JSON body (HTML 500 page) must
    // land in the catch below, not escape as a rejected promise.
    //
    // `unreachable` last so the client's verdict always wins: this flag decides
    // whether a prospect is waved through a Skip-less gate, so a stray key in a
    // backend payload must not be able to flip it.
    return { ...(await r.json()), unreachable: false };
  } catch (err) {
    console.error('agentInsightsRedeemPromo error', err);
    return { recorded: false, valid: false, partner: null, unreachable: true };
  }
};

// --- Model Router onboarding (per-org latch) --------------------------------
// Mirrors the Agent Insights onboarding pair above: a one-way per-org latch
// that gates the /model-router route between its onboarding flow and the
// hand-off to the Agent Insights dashboard.

export const fetchModelRouterOnboardingStatus = async (): Promise<{
  success: boolean;
  completed: boolean;
}> => {
  const r = await getWithAuth(`${apiUrl}modelRouterOnboardingStatus/`);
  if (!r.ok) throw new Error(`modelRouterOnboardingStatus failed: ${r.status}`);
  return r.json();
};

/** Billing mode chosen once during onboarding. One-shot; not user-editable after. */
export type ModelRouterBillingMode = 'proxy' | 'byok' | 'byoc';

/**
 * A BYOK credential slot, or ``'multi'``.
 *
 * ``'multi'`` is what the BYOK UI writes today: the org holds a key per provider
 * it has an account with, and the router picks whichever key serves the model it
 * routed to. The single-provider values remain valid — orgs configured before
 * multi-key BYOK still store one — and ``'bedrock' | 'gcp' | 'vertex'`` are
 * legacy-display only (no longer offered, rejected on write).
 */
export type ModelRouterByokProvider =
  | 'multi'
  | 'openai'
  | 'anthropic'
  | 'google'
  | 'xai'
  | 'fireworks'
  | 'together'
  | 'baseten'
  | 'openrouter'
  | 'azure-foundry'
  | 'bedrock'
  | 'gcp'
  | 'vertex';

/**
 * BYOK credentials as sent to the write endpoints.
 *
 * Multi-key (``byokProvider: 'multi'``) sends the nested form:
 * ``{ providers: { openai: { apiKey }, fireworks: { apiKey, baseUrl? } } }``.
 * A single-provider write sends that provider's fields flat
 * (``{ apiKey }``, or ``{ endpoint, apiKey, apiVersion }`` for Azure Foundry).
 * Build it with ``buildByokCredentials`` rather than by hand.
 */
export type ModelRouterByokCredentials =
  | Record<string, string>
  | { providers: Record<string, Record<string, string>> };

export const completeModelRouterOnboarding = async (payload: {
  mode: ModelRouterBillingMode;
  /** Required when mode == 'byok'. */
  byokProvider?: ModelRouterByokProvider;
  /** Required when mode == 'byok'. Encrypted on the backend at rest. */
  credentials?: ModelRouterByokCredentials;
  /** BYOC only — the customer's internal router URL. */
  byocProxyUrl?: string;
}): Promise<{
  success: boolean;
  completed: boolean;
  mode: ModelRouterBillingMode;
}> => {
  const r = await postWithAuth(
    `${apiUrl}modelRouterCompleteOnboarding/`,
    payload
  );
  if (!r.ok)
    throw new Error(`modelRouterCompleteOnboarding failed: ${r.status}`);
  return r.json();
};

/**
 * Swap the BYOK provider + credentials for an already-BYOK org. Backend
 * atomically wipes the old encrypted blob and writes the new one; routing
 * keeps working through the swap because the gateway reads creds per turn.
 * Rejects with 409 if the org isn't on BYOK (cannot change what isn't set).
 */
export const replaceByokProvider = async (payload: {
  byokProvider: ModelRouterByokProvider;
  credentials: ModelRouterByokCredentials;
}): Promise<{
  success: boolean;
  byokProvider: ModelRouterByokProvider;
}> => {
  const r = await postWithAuth(
    `${apiUrl}modelRouterReplaceByokProvider/`,
    payload
  );
  if (!r.ok) throw new Error(`replaceByokProvider failed: ${r.status}`);
  return r.json();
};

/**
 * Switch an already-onboarded org's billing mode (proxy ↔ byok). Distinct
 * from ``completeModelRouterOnboarding`` (one-shot, initial setup) and
 * ``replaceByokProvider`` (BYOK-internal provider swap): this is what the
 * Settings tab calls when the user toggles between modes.
 *
 * Switching TO proxy clears stored BYOK credentials on the backend in the
 * same transaction. Switching TO byok requires a fresh provider + credential
 * pair (never reuses whatever was there before).
 *
 * 409 = org never finished onboarding; 400 = unknown mode / bad BYOK inputs.
 *
 * Failures throw {@link RouterModeSwitchError}, which carries the backend's own
 * message and code. The backend writes that copy to be shown (see the error
 * contract in the repo's CLAUDE.md); throwing it away and substituting a guess
 * is how "Finish Model Router onboarding before switching modes" reached users
 * as "Check the credentials and try again" — advice that sent them to re-enter
 * a key that was never the problem.
 */
export class RouterModeSwitchError extends Error {
  /** Stable machine code, e.g. "not_onboarded". Empty when the body had none. */
  readonly code: string;
  readonly status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'RouterModeSwitchError';
    this.code = code;
    this.status = status;
  }
}

export const switchModelRouterBillingMode = async (payload: {
  mode: ModelRouterBillingMode;
  /** Required when mode == 'byok'. */
  byokProvider?: ModelRouterByokProvider;
  /** Required when mode == 'byok'. Same shape as onboarding. */
  credentials?: ModelRouterByokCredentials;
}): Promise<{
  success: boolean;
  mode: ModelRouterBillingMode;
}> => {
  const r = await postWithAuth(
    `${apiUrl}modelRouterSwitchBillingMode/`,
    payload
  );
  if (!r.ok) {
    // Body first, status second. A 409 here always carries a specific reason;
    // the status alone cannot tell the user what to do about it.
    const body = await r.json().catch(() => null);
    throw new RouterModeSwitchError(
      body?.error || `Couldn't switch billing mode (${r.status}).`,
      body?.code || '',
      r.status
    );
  }
  return r.json();
};

// --- Model Router dashboard / settings --------------------------------------

export type ModelRouterLimitState = 'ok' | 'warning' | 'reached';

export interface ModelRouterRoutingPolicy {
  active: boolean;
  baselineSpendUsd: number;
  sessionsAutoRoutedPct: number;
  monthlySavingsUsd: number;
  /** ACTUAL router spend: provider cost + our fee over every routed turn.
   * This is what a "Total Spend" tile must bind to — it is scoped to the router
   * and nothing else. Coding-agent spend at large (Cursor, direct Claude Code)
   * lives in agent-insights and is a different, much larger number. */
  routerSpendUsd: number;
  /** @deprecated Misleading twice over: it is not a projection (the money is
   * already spent), and it counts only turns that recorded a savings baseline.
   * Use routerSpendUsd. */
  projectedSpendUsd: number;
}

/** One bucket of the usage-over-time series.
 *
 * Replaces the old baseline-vs-actual savings series: the chart now plots what the
 * customer used, with no comparison model involved. Note the population also
 * differs — the savings series only covered turns that recorded a baseline, which
 * would silently drop routed turns from the customer's own spend. */
export interface ModelRouterUsagePoint {
  date: string;
  /** Spend in this bucket: provider cost + our fee. */
  costUsd: number;
  turns: number;
  /** WHOLE prompt sent, cached portion included. */
  promptTokens: number;
  completionTokens: number;
  cachedTokens: number;
  cacheWriteTokens: number;
}

export interface ModelRouterModelUsage {
  model: string;
  /** Share of routed prompt plus completion tokens; cached input is included once. */
  usagePct: number;
  /** Provider cost plus router fee for this model's routed turns. */
  costUsd?: number;
}

export interface ModelRouterDashboardResponse {
  success: boolean;
  lastUpdated: string;
  routingPolicy: ModelRouterRoutingPolicy;
  usageOverTime: ModelRouterUsagePoint[];
  modelUsage: ModelRouterModelUsage[];
  limitState: ModelRouterLimitState;
}

export interface ModelRouterTransaction {
  date: string;
  creditsUsd: number;
  boughtBy: string;
  /** Buyer's PFP URL when resolvable; falls through to AvatarFallback initial. */
  boughtByImage?: string | null;
  type: string;
  paymentRef?: string; // Stripe ref; resolved to a receipt URL on "View Invoice"
}

export interface ModelRouterSettingsResponse {
  success: boolean;
  /** Billing mode as of onboarding — hides the credits section when 'byok'. */
  mode: ModelRouterBillingMode;
  /** The customer's BYOK provider when mode==='byok'; null otherwise. */
  byokProvider?: ModelRouterByokProvider | null;
  balanceUsd: number;
  updatedAt: string;
  note: string;
  rechargeRequired: boolean;
  transactions: ModelRouterTransaction[];
}

export type ModelRouterRange = '1d' | '1w' | '1m' | '3m' | '6m' | '1y';

/** Bucket size for the savings-over-time series. */
export type ModelRouterGranularity = 'day' | 'week' | 'month';

/** Scope for the savings-over-time series. */
export interface ModelRouterDashboardOptions {
  range?: ModelRouterRange;
  granularity?: ModelRouterGranularity;
  /** When set, chart narrows to a single developer's traffic. */
  userUuid?: string | null;
  /** When set (and userUuid isn't), chart narrows to a team's traffic. */
  teamUuid?: string | null;
}

/**
 * Thrown by the Model Router / Model Quality reads below on a non-2xx
 * response. Carries the backend's stable `code` (backend PR #5488:
 * `{"success": false, "error": "...", "code": "access_denied"}` when a Member
 * names someone else, or hits an admin-only endpoint) so a caller can render
 * a plain "no access" empty state instead of a generic retry card, and skip
 * react-query's retry on that one code -- a permission failure will not
 * resolve itself on a second attempt.
 */
export class ModelRouterAccessError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'ModelRouterAccessError';
    this.code = code;
    this.status = status;
  }
}

/** True for the specific "you don't have access" failure, not any other
 * error (a real outage still gets the retryable LoadFailedCard). */
export function isAccessDenied(error: unknown): boolean {
  return error instanceof ModelRouterAccessError && error.code === 'access_denied';
}

async function throwModelRouterError(r: Response, label: string): Promise<never> {
  const body = await r.json().catch(() => null);
  throw new ModelRouterAccessError(
    body?.error || `${label} failed: ${r.status}`,
    body?.code || '',
    r.status
  );
}

export const fetchModelRouterDashboard = async (
  opts: ModelRouterDashboardOptions = {}
): Promise<ModelRouterDashboardResponse> => {
  const params = new URLSearchParams();
  params.set('range', opts.range ?? '1m');
  params.set('granularity', opts.granularity ?? 'day');
  if (opts.userUuid) params.set('userUuid', opts.userUuid);
  else if (opts.teamUuid) params.set('teamUuid', opts.teamUuid);
  const r = await getWithAuth(`${apiUrl}modelRouterDashboard/?${params}`);
  if (!r.ok) await throwModelRouterError(r, 'modelRouterDashboard');
  return r.json();
};

export const fetchModelRouterSettings =
  async (): Promise<ModelRouterSettingsResponse> => {
    const r = await getWithAuth(`${apiUrl}modelRouterSettings/`);
    if (!r.ok) throw new Error(`modelRouterSettings failed: ${r.status}`);
    return r.json();
  };

export const setModelRouterActive = async (
  active: boolean
): Promise<{ success: boolean; active: boolean }> => {
  const r = await postWithAuth(`${apiUrl}modelRouterSetActive/`, { active });
  if (!r.ok) throw new Error(`modelRouterSetActive failed: ${r.status}`);
  return r.json();
};

// --- Model Router sessions (the Sessions tab) -------------------------------
// One row per routed coding-agent session, straight from the billing ledger
// (grouped by session_id): the developer who ran it, token totals, real billed
// cost, and savings vs the Opus baseline.

export interface ModelRouterSessionRow {
  sessionId: string;
  userUuid: string | null;
  userName: string | null;
  userImage: string | null;
  startTime: string | null;
  inputTokens: string;
  inputTokensRaw: number;
  outputTokens: string;
  outputTokensRaw: number;
  cachedTokens: string;
  cachedTokensRaw: number;
  /** Tokens written to cache. Billed at a PREMIUM (1.25x fresh input), and until
   * now invisible — they were recorded server-side but never surfaced. */
  cacheWriteTokens: string;
  cacheWriteTokensRaw: number;
  /** The portion of inputTokens that bills at FULL price:
   * inputTokens − cachedTokens − cacheWriteTokens. */
  freshInputTokens: string;
  freshInputTokensRaw: number;
  /** inputTokens + outputTokens. */
  totalTokens: string;
  totalTokensRaw: number;
  cost: string;
  costRaw: number;
  saved: string;
  savedRaw: number;
  turns: number;
}

export const fetchModelRouterSessions = async (
  range: ModelRouterRange = '1m'
): Promise<{
  success: boolean;
  sessions: ModelRouterSessionRow[];
  count: number;
  // True when the org has more sessions than the server cap — the list (and
  // the totals) cover only the newest `limit` sessions. Optional so the FE
  // keeps working against older backends that don't send these fields.
  truncated?: boolean;
  limit?: number;
  totalCost: string;
  totalCostRaw: number;
  totalSaved: string;
  totalSavedRaw: number;
}> => {
  const r = await getWithAuth(
    `${apiUrl}modelRouterSessions/?range=${encodeURIComponent(range)}`
  );
  if (!r.ok) await throwModelRouterError(r, 'modelRouterSessions');
  return r.json();
};

// One routed request (turn) within a session — the session drill-down table.
export interface ModelRouterMemberRow {
  userUuid: string | null;
  userName: string | null;
  userImage: string | null;
  sessions: number;
  spend: string;
  spendRaw: number;
  saved: string;
  savedRaw: number;
  savedPct: number;
  /** Recent daily savings, ascending — the inline sparkline. */
  trend: number[];
}

export const fetchModelRouterMembers = async (
  range: ModelRouterRange = '1m'
): Promise<{
  success: boolean;
  members: ModelRouterMemberRow[];
  count: number;
  totalSaved: string;
  totalSavedRaw: number;
  totalSpend: string;
  totalSpendRaw: number;
}> => {
  const r = await getWithAuth(
    `${apiUrl}modelRouterMembers/?range=${encodeURIComponent(range)}`
  );
  if (!r.ok) await throwModelRouterError(r, 'modelRouterMembers');
  return r.json();
};

export const fetchModelRouterMemberDetail = async (
  userUuid: string,
  range: ModelRouterRange = '1m'
): Promise<{
  success: boolean;
  userUuid: string;
  usageOverTime: ModelRouterUsagePoint[];
  totalSaved: string;
  totalSavedRaw: number;
  totalSpend: string;
  totalSpendRaw: number;
  sessions: number;
}> => {
  const r = await getWithAuth(
    `${apiUrl}modelRouterMemberDetail/?user_uuid=${encodeURIComponent(userUuid)}&range=${encodeURIComponent(range)}`
  );
  if (!r.ok) await throwModelRouterError(r, 'modelRouterMemberDetail');
  return r.json();
};

// --- Agent Insights coaching (per-developer, Haiku-generated) ---------------
// Metric-grounded suggestions for driving the coding agent better. A developer
// sees their own; an admin can request any org member's via user_uuid.

export type AgentCoachingFixType = 'claude_md' | 'prompt' | 'workflow';

export interface AgentCoachingSuggestion {
  title: string;
  severity: 'high' | 'medium' | 'low';
  evidence: string;
  recommendation: string;
  fix: string;
  fix_type: AgentCoachingFixType;
  difficulty?: 'quick' | 'moderate' | 'ambitious';
}

export interface AgentCoaching {
  headline: string;
  persona?: string;
  suggestions: AgentCoachingSuggestion[];
  wins?: string[];
}

export interface AgentInsightsCoachingResponse {
  success: boolean;
  coaching: AgentCoaching | null;
  sessions: number;
  model?: string | null;
  generated_at?: string | null;
  error?: string;
  code?: string;
}

// --- Agent maturity (per-engineer + org "are they wielding it well") --------

export interface AgentMaturityCapability {
  key: string;
  label: string;
  used: boolean;
}

export interface AgentMaturityDev {
  userUuid: string;
  name: string;
  image: string | null;
  role: string;
  sessions: number;
  score: number;
  band: string;
  breadth: number;
  /** False when the member's client shipped no tool telemetry — breadth is
   * UNKNOWN, not zero, and their score was renormalised without it. Absent on
   * older backend responses (treat as measured). */
  breadthMeasured?: boolean;
  discipline: number;
  hygiene: number;
  /** False when no session in the window produced an efficiency score:
   * hygiene is absent, not zero, and the score was renormalised without it.
   * Absent on older backend responses (treat as measured). */
  hygieneMeasured?: boolean;
  capabilities: AgentMaturityCapability[];
  skillsUsed: string[];
}

/** One org/team improvement suggestion — same shape as the per-member
 *  coaching suggestions so the same popover renders every level. */
export interface AgentLevelCoachingSuggestion {
  title: string;
  severity: 'high' | 'medium' | 'low';
  evidence?: string;
  recommendation?: string;
}

export interface AgentMaturityCoaching {
  org: AgentLevelCoachingSuggestion[];
  teams: {
    teamUuid: string;
    teamName: string;
    suggestions: AgentLevelCoachingSuggestion[];
  }[];
}

export interface AgentMaturityResponse {
  success: boolean;
  org: {
    score: number;
    band: string;
    developers: number;
    bands: Record<string, number>;
    coverage: { key: string; label: string; pct: number; count: number }[];
  } | null;
  developers: AgentMaturityDev[];
  /** Org + team "How to improve" (backend #4133); absent until it deploys. */
  coaching?: AgentMaturityCoaching;
}

// --- Agent Insights Team Health (lean 5-tile snapshot) ----------------------
// Single endpoint replacing the agentInsightsTeam/ + agentInsightsMaturity/
// pair for the Agent Insights tab's stat tiles. Org-scoped, admin-only, no params.

/**
 * Stable identity for each driving-style signal. The UI keys its plain-language
 * name, explanation and healthy range off this rather than off `label`, so
 * relabelling a metric can't silently detach it from its guidance.
 */
export type DrivingStyleKey =
  | 'toolCallsPerTurn'
  | 'readFirstRate'
  | 'readsPerEdit'
  | 'researchPerAction'
  | 'fullRewrites'
  | 'retryRate';

export interface DrivingStyleTile {
  key: DrivingStyleKey;
  /** Short lowercase label, kept for Ask Ellie context and the home widget. */
  label: string;
  /** Display-formatted value, or '-' when the signal was never measured. */
  value: string;
  /**
   * The number behind `value`, for banding against a healthy range. Null means
   * the backend did not measure this signal in the window, which is NOT the
   * same as zero: `readFirstRate`/`fullRewrites` arrive null on orgs whose
   * sessions predate the instrumentation, and rendering that as "0%" reads as
   * "your team never reads code before editing".
   */
  raw: number | null;
  danger: boolean;
}

export interface AgentInsightsTeamHealthResponse {
  success: boolean;
  orgMaturity: { score: number; band: string } | null;
  avgEfficiency: number;
  tokens: string;
  costPerDev: string;
  /** Retry rate (%), null when the graph never measured tool calls. */
  retryRate: number | null;
  membersActive: number;
  membersTotal: number;
  trend: { date: string; efficiency: number }[];
  byProject: {
    name: string;
    sessions: number;
    tokens: string;
    spend: string;
    spendRaw: number;
    /** Origin of the project (e.g. "github", "jira", "linear", "confluence").
     *  Drives the row's source logo; falls back to the org VCS provider. */
    source?: string;
  }[];
  drivingStyle: DrivingStyleTile[];
  /** Declared window served by overview/efficiency (falls back to the spend
   *  endpoint's). Backend window-param PR — absent today. */
  window?: OverviewWindowField;
  error?: string;
  code?: string;
}

// Shapes returned by the graph-backed Overview endpoints we consume here.
interface OverviewHealth {
  orgMaturity: { score: number; band: string } | null;
  avgEfficiency: number | null;
  tokens: string;
  costPerDev: string;
  retryRate: number | null;
  membersActive: number;
  membersTotal: number;
}
interface OverviewEfficiency {
  trend: { month: string; efficiency: number }[];
  window?: OverviewWindowField;
}
interface OverviewDrivingStyle {
  toolCallsPerTurn: number;
  readsPerEdit: number;
  researchPerAction: number;
  retryRate: number | null;
  readFirstRate: number | null;
  fullRewrites: number | null;
}

const num = (x: number | null | undefined): number =>
  typeof x === 'number' ? x : 0;

/** Percent, or a plain hyphen when the signal was not measured (never "0%"). */
const pctOrDash = (x: number | null | undefined, digits = 0): string =>
  typeof x === 'number' ? `${x.toFixed(digits)}%` : '-';

// --- Agent Insights Overview Spend (org totals + by-project/repo/model) -----

export interface AgentInsightsSpendFacetRow {
  label: string;
  tokens: string;
  tokensRaw: number;
  sessions: number;
  spend: string;
  spendRaw: number;
  /** Stable Linear/Jira project id (project facet only) — the budget join key.
   *  Absent/null for unattributed rows and the repo/model facets. */
  projectId?: string | null;
  /** ISO timestamp of the most recent session (repo facet only) — powers the
   *  repo table's "Last activity" column. Absent when undated / other facets. */
  lastActive?: string | null;
}

export interface AgentInsightsSpendTrendPoint {
  /** Time bucket, at the grain the series is served: 'YYYY-MM-DD' for the
   *  day-grain series (overall / byModel / byUser), 'YYYY-MM' for monthly
   *  ones (byRepo / byTeam). Named `month` for the original monthly-only
   *  contract. */
  month: string;
  /** Facet label (team / model / repo name; 'Not Assigned' for unmapped). */
  label?: string;
  spend: string;
  spendRaw: number;
}

export interface AgentInsightsOverviewSpendResponse {
  success: boolean;
  totals: {
    sessions: number;
    tokens: string;
    tokensRaw: number;
    cost: string;
    costRaw: number;
    members: number;
    /** True when usage-bearing model groups carry $0 — the catalog couldn't
     *  price them, so `cost` is an UNDERcount and the UI must flag it as
     *  approximate instead of presenting it as exact (audit D12). */
    costApprox?: boolean;
    /** Raw token count sitting in those unpriced groups — the size of the
     *  money the total can't see. Powers the Unpriced bucket. */
    unpricedTokensRaw?: number;
  };
  /** Spend series for the trend tabs — graph-served, includes the
   *  'Not Assigned' bucket so per-facet sums total to `overall`. Grain per
   *  series: see `AgentInsightsSpendTrendPoint.month`. */
  trend?: {
    overall?: AgentInsightsSpendTrendPoint[];
    byModel?: AgentInsightsSpendTrendPoint[];
    byHarness?: AgentInsightsSpendTrendPoint[];
    byRepo?: AgentInsightsSpendTrendPoint[];
    byTeam?: AgentInsightsSpendTrendPoint[];
  };
  byProject: AgentInsightsSpendFacetRow[];
  byRepo: AgentInsightsSpendFacetRow[];
  byModel: AgentInsightsSpendFacetRow[];
  /**
   * Same-window breakdown of byRepo's single "Unattributed" row (ENT-4204):
   * one row per activity_class bucket ("Non-repo work" / "Attribution
   * pending"), same shape as any AgentInsightsSpendFacetRow, so the FE can
   * splice these in place of that row instead of showing it undifferentiated.
   * Absent on an older cached payload (pre-v7) — treat as "no breakdown yet",
   * not an error.
   */
  nonRepoBreakdown?: {
    rows: AgentInsightsSpendFacetRow[];
    /** Percent of the window's tokens attributed to a real repo, or null
     *  when the org has no session tokens in the window at all (a "0%"
     *  there would read as a measured number rather than "no data yet"). */
    attributionCoveragePct: number | null;
  };
  /** Declared time window served (backend window-param PR; absent today). */
  window?: OverviewWindowField;
  error?: string;
  code?: string;
}

// --- Agent Insights feed (ranked, guardrailed findings) ---------------------

export interface AgentInsightFinding {
  id: string;
  kind:
    | 'model'
    | 'cost'
    | 'workload'
    | 'efficiency'
    | 'quality'
    | 'time'
    | 'coverage';
  severity: 'high' | 'medium' | 'info';
  title: string;
  detail: string;
  metric: string | null;
}

export const fetchAgentInsightsFindings = async (): Promise<{
  success: boolean;
  findings: AgentInsightFinding[];
  /** ISO-8601 UTC time the backend rendered these findings. The payload is
   *  served from a cache for up to a day, so this, not the fetch time, is how
   *  old the data is. Absent on a payload cached before the field existed. */
  generated_at?: string;
}> => {
  const r = await getWithAuth(`${apiUrl}agentInsightsFindings/`);
  if (!r.ok) throw new Error(`agentInsightsFindings failed: ${r.status}`);
  return r.json();
};

// --- Skill drill-down (who uses a given skill + their stats) ----------------

export interface AgentSkillUser {
  userUuid: string;
  name: string;
  image: string | null;
  count: number;
  sessions: number;
  avgEfficiency: number | null;
}

export interface AgentSkillUsageResponse {
  success: boolean;
  skill: string;
  text: string | null;
  users: AgentSkillUser[];
  totalUses: number;
  developers: number;
}

export const fetchAgentInsightsSkill = async (
  name: string
): Promise<AgentSkillUsageResponse> => {
  const r = await getWithAuth(
    `${apiUrl}agentInsightsSkill/?name=${encodeURIComponent(name)}`
  );
  if (!r.ok) throw new Error(`agentInsightsSkill failed: ${r.status}`);
  return r.json();
};

// --- Agent Insights session drill-down (project -> sessions -> detail) ------

export interface AgentSessionRow {
  sessionId: string;
  sessionTitle?: string | null;
  userUuid: string;
  userName: string | null;
  userImage: string | null;
  agent: string;
  project: string | null;
  repositories?: string[];
  activityClass?: string | null;
  conductorWorkspace?: string | null;
  model: string | null;
  startTime: string | null;
  /** Latest observed task activity; absent on legacy/session-only rows. */
  lastWorkedAt?: string | null;
  taskCount?: number | null;
  sessionIdentity?: AgentSessionIdentity | null;
  tokens: string;
  tokensRaw: number;
  inputTokens: string;
  inputTokensRaw: number;
  outputTokens: string;
  outputTokensRaw: number;
  cachedTokens: string;
  cachedTokensRaw: number;
  cost: string;
  costRaw: number;
  saved: string;
  savedRaw: number;
  efficiency: number | null;
  toolsPerTurn: number;
  errorRate: number;
  turns: number;
  usageState?: string | null;
}

export interface AgentSessionDetail {
  sessionId: string;
  userUuid: string;
  agent: string;
  project: string | null;
  model: string | null;
  startTime: string | null;
  endTime: string | null;
  efficiency: number | null;
  // Cache-aware session cost; `costApprox` true when the cache split was
  // missing (legacy CLI) and the figure is an upper bound.
  cost: string;
  costRaw: number;
  costApprox: boolean;
  tokens: {
    total: string;
    prompt: string;
    completion: string;
    cacheRead: string;
    cacheWrite: string;
    promptPct: number;
  };
  quality: { label: string; value: string }[];
  waste: { label: string; value: string }[];
  signals: Record<string, number>;
  tools: { name: string; count: number }[];
  skills: { name: string; count: number; text?: string | null }[];
  usageState?: string | null;
  hero: {
    turns: number;
    toolsPerTurn: number;
    toolCalls: number;
    errors: number;
    errorRate: number;
    durationMs: number;
    insightsCount: number;
  };
  contextGrowth: {
    turn: number;
    promptTokens: number;
    durationMs: number;
    interrupted: boolean;
  }[];
  tasks: {
    name: string | null;
    startTurn: number | null;
    endTurn: number | null;
    tokens: string;
    waste: string;
  }[];
  insights: {
    category: string;
    title: string;
    content: string;
    severity: 'critical' | 'warning' | 'info' | string;
  }[];
}

const agentInsightsSessionsEndpoint = 'agentInsightsSessions/';

export interface AgentSessionsResponse {
  success: boolean;
  sessions: AgentSessionRow[];
  /** Page-local count for cursor responses; legacy responses use total rows. */
  count: number;
  /** Present on the superseded numbered-paging contract. */
  page?: number;
  pageSize?: number;
  totalPages?: number;
  /** Opaque continuation token from the keyset-paging contract. */
  nextCursor?: string | null;
  hasMore?: boolean;
  totalCost: string;
  totalCostRaw: number;
  totalSaved?: string;
  totalSavedRaw?: number;
}

export const fetchAgentSessions = async (params: {
  project?: string;
  userUuid?: string;
  days?: number | null;
  page?: number;
  pageSize?: number;
  search?: string;
  cursor?: string;
}): Promise<AgentSessionsResponse> => {
  const qs = new URLSearchParams();
  if (params.project) qs.set('project', params.project);
  if (params.userUuid) qs.set('user_uuid', params.userUuid);
  if (params.days != null) qs.set('days', String(params.days));
  if (params.page != null && !params.cursor) {
    qs.set('page', String(params.page));
  }
  if (params.pageSize != null) qs.set('pageSize', String(params.pageSize));
  if (params.search?.trim()) qs.set('search', params.search.trim());
  if (params.cursor) qs.set('cursor', params.cursor);
  const r = await getWithAuth(
    `${apiUrl}${agentInsightsSessionsEndpoint}?${qs.toString()}`
  );
  if (!r.ok) throw new Error(`agentInsightsSessions failed: ${r.status}`);
  return r.json();
};

export const fetchAgentSessionDetail = async (
  sessionId: string
): Promise<{ success: boolean; session: AgentSessionDetail | null }> => {
  const r = await getWithAuth(
    `${apiUrl}${agentInsightsSessionsEndpoint}?session_id=${encodeURIComponent(sessionId)}`
  );
  if (!r.ok) throw new Error(`agentInsightsSession failed: ${r.status}`);
  return r.json();
};

const agentInsightsCoachingEndpoint = 'agentInsightsCoaching/';

export const fetchAgentInsightsCoaching = async (
  userUuid?: string
): Promise<AgentInsightsCoachingResponse> => {
  /** Fetch metric-grounded coaching. Omit userUuid for the caller's own; an
   * admin passes a member's uuid (non-admins get 403 for anyone but self). */
  const qs = userUuid ? `?user_uuid=${encodeURIComponent(userUuid)}` : '';
  const response = await getWithAuth(
    apiUrl + agentInsightsCoachingEndpoint + qs
  );
  if (!response.ok)
    throw new Error(`agentInsightsCoaching failed: ${response.status}`);
  return response.json();
};

// --- Agent Session Activity Map (heatmap) -----------------------------------
// Contract: docs/agent-session-activity-map.contract.md — GET
// agentInsights/sessionActivityMap/. Filtering, search, and pagination are all
// server-side; every param is a query string.

export interface AgentActivityBucket {
  date: string;
  label: string;
}

export type AgentActivityOutcome = 'completed' | 'errored' | 'partial';

export interface AgentActivityCell {
  date: string;
  /** Most recent day with sessions inside this week bucket (exact date);
   *  null/absent for empty cells or until the backend change deploys. */
  last?: string | null;
  sessions: number;
  level: number;
  model: string;
  tokens: string;
  tokensRaw: number;
  efficiency: number;
  outcome: AgentActivityOutcome;
  errorRate: number;
  repos: string[];
}

export interface AgentActivityRow {
  id: string;
  name: string;
  avatarUrl: string | null;
  cells: AgentActivityCell[];
}

export interface AgentActivityFilterOption {
  id: string;
  label: string;
}

export interface AgentSessionActivityMapParams {
  /** Owner's single-team scope; sent as `team_uuid`, omitted when unset. */
  teamUuid?: string | null;
  groupBy: 'member' | 'team';
  repository: string;
  tool: string;
  search: string;
  page: number;
  pageSize: number;
}

export interface AgentSessionActivityMapResponse {
  success: boolean;
  groupBy: 'member' | 'team';
  buckets: AgentActivityBucket[];
  rows: AgentActivityRow[];
  page: number;
  pageSize: number;
  totalRows: number;
  totalPages: number;
  filters: {
    repositories: AgentActivityFilterOption[];
    tools: AgentActivityFilterOption[];
  };
}

export const fetchAgentSessionActivityMap = async (
  params: AgentSessionActivityMapParams
): Promise<AgentSessionActivityMapResponse> => {
  const qs = new URLSearchParams({
    groupBy: params.groupBy,
    repository: params.repository,
    tool: params.tool,
    search: params.search,
    page: String(params.page),
    pageSize: String(params.pageSize),
  });
  if (params.teamUuid) qs.set('team_uuid', params.teamUuid);
  const r = await getWithAuth(
    `${apiUrl}agentInsights/sessionActivityMap/?${qs}`
  );
  if (!r.ok)
    throw new Error(`agentInsights/sessionActivityMap failed: ${r.status}`);
  return r.json();
};

// --- Agent Insights Overview Bundle (all 9 overview sections, one request) -
// Consolidates health/spend/team/usage/maturity/efficiency/driving-style/
// context-health/token-waste into one round trip (Overview re-architecture).
// Each section is byte-identical to the payload of the standalone endpoint
// it replaces.

/** Raw wire shape of GET overview/maturity, before the legacy
 *  {org, developers} remap `fetchAgentInsightsMaturity` used to apply. */
export interface OverviewMaturityRaw {
  orgMaturity: AgentMaturityResponse['org'];
  byMember: AgentMaturityDev[];
  coaching?: AgentMaturityCoaching;
}

/** Every section is nullable: the backend isolates each section builder, so one
 *  failing graph read comes back `null` (with its key in `failedSections`)
 *  instead of failing the whole request. Consumers see the same `undefined`
 *  they already handle for "no data yet". */
export interface AgentInsightsOverviewBundleSections {
  health: OverviewHealth | null;
  spend: AgentInsightsOverviewSpendResponse | null;
  team: AgentInsightsTeamResponse | null;
  usage: AgentInsightsUsageResponse | null;
  /** Bundle-only — no standalone `/overview/` endpoint (see 06-apis.md). */
  peopleModels: AgentInsightsPeopleModelsResponse | null;
  maturity: OverviewMaturityRaw | null;
  efficiency: OverviewEfficiency | null;
  drivingStyle: OverviewDrivingStyle | null;
  /** Not consumed by any FE surface today; shape not modeled. */
  contextHealth: unknown;
  /** Not consumed by any FE surface today; shape not modeled. */
  tokenWaste: unknown;
}

/** The two per-page gates, answered by the bundle itself.
 *
 *  The backend folds `onboardingStatus` and `dataStatus` into the bundle
 *  response precisely so the page need not ask for them separately — they are
 *  single-row RDS reads it is already positioned to do. Each is `null` when
 *  its read failed, which callers must read as "unknown, fall back to the
 *  standalone endpoint" rather than as a negative answer.
 *
 *  Neither carries the `success` flag its standalone endpoint wraps it in;
 *  these are the payloads themselves. */
export interface AgentInsightsOverviewBundleGates {
  onboardingStatus: { completed: boolean } | null;
  dataStatus: Omit<AgentInsightsDataStatusResponse, 'success'> | null;
}

export interface AgentInsightsOverviewBundleResponse {
  success: boolean;
  sections: AgentInsightsOverviewBundleSections;
  /** Section keys whose builder failed server-side; their `sections` entry is
   *  `null`. Empty or absent when every section built. */
  failedSections?: string[];
  /** Absent on a backend older than the gate-folding change; consumers keep
   *  their standalone-endpoint fallback for that case. */
  gates?: AgentInsightsOverviewBundleGates;
  window?: OverviewWindowField;
  error?: string;
  code?: string;
}

/** `days` = trailing window (Overview window selector); omitted = all time.
 *  Replaces 9 standalone `overview/*` calls with one request. */
export const fetchAgentInsightsOverviewBundle = async (
  _org_uuid: string,
  days?: number | null,
  teamUuid?: string | null
): Promise<AgentInsightsOverviewBundleResponse> => {
  const r = await getWithAuth(
    // Keep this critical dashboard read same-origin so it always traverses
    // the Next BFF and receives the httpOnly-session-derived backend token.
    `/api/be/agentInsights/overview/bundle${overviewDaysQuery(days, teamUuid)}`
  );
  if (!r.ok)
    throw new Error(`agentInsights/overview/bundle failed: ${r.status}`);
  return r.json() as Promise<AgentInsightsOverviewBundleResponse>;
};

/** Read a heavy page's required section without waiting for the whole bundle. */
export async function fetchAgentInsightsOverviewSection<
  K extends 'team' | 'spend' | 'maturity',
>(
  _orgUuid: string,
  days: number | null | undefined,
  section: K,
  teamUuid?: string | null
): Promise<NonNullable<AgentInsightsOverviewBundleSections[K]>> {
  const response = await getWithAuth(
    `${apiUrl}agentInsights/overview/${section}/${overviewDaysQuery(days, teamUuid)}`
  );
  if (!response.ok)
    throw new Error(`Overview section failed: ${response.status}`);
  const payload = await response.json();
  if (payload.success === false) throw new Error('Overview section failed');
  return payload;
}

// --- Agent Insights members grid (one call for roster + proficiency band) --
// Replaces the members grid's separate `overview/team` + `overview/maturity`
// reads: same member row shape as `team.members[i]`, plus `band` (the
// maturity band already computed server-side, so the FE no longer derives it
// from a score). Other `team`/`maturity` consumers are untouched.
export type AgentInsightsMemberRow = AgentInsightsTeamMember & {
  band: string | null;
  /** Raw 0-100 maturity score `band` is derived from; sort on this, not the
   *  band label, to match the old `overview/maturity` byMember ordering. */
  maturityScore: number | null;
};

export interface AgentInsightsMembersResponse {
  success: boolean;
  members: AgentInsightsMemberRow[];
  membersNotSynced: NonNullable<AgentInsightsTeamResponse['membersNotSynced']>;
  window?: OverviewWindowField;
  error?: string;
  code?: string;
}

export const fetchAgentInsightsMembers = async (
  days?: number | null,
  teamUuid?: string | null
): Promise<AgentInsightsMembersResponse> => {
  const r = await getWithAuth(
    `${apiUrl}agentInsights/members/${overviewDaysQuery(days, teamUuid)}`
  );
  if (!r.ok) throw new Error(`agentInsights/members failed: ${r.status}`);
  const payload: AgentInsightsMembersResponse = await r.json();
  // Same contract as fetchAgentInsightsOverviewSection: a success:false body
  // is a failed load, not an empty grid.
  if (payload.success === false)
    throw new Error(payload.error ?? 'agentInsights/members failed');
  return payload;
};

// --- Token-waste-reduction suggestions ("Suggest fixes" button) -----------
export interface TokenWasteSuggestion {
  /** One of the waste cause labels, or '' when the model named none. */
  cause: string;
  title: string;
  detail: string;
}

export interface TokenWasteSuggestionsResponse {
  success: boolean;
  suggestions: TokenWasteSuggestion[];
  total: number;
  /** null when the rule-based fallback was served (model call failed). */
  model: string | null;
  generatedAt: string;
  /** false = rule-based fallback (still actionable, less tailored). */
  grounded: boolean;
  error?: string;
  code?: string;
}

/** POST (not GET): fires only on button click, generates once per (org,
 *  window) per hour server-side. `days` follows the Overview window selector. */
export const fetchTokenWasteSuggestions = async (
  days?: number | null,
  teamUuid?: string | null
): Promise<TokenWasteSuggestionsResponse> => {
  const r = await postWithAuth(
    `${apiUrl}agentInsights/overview/token-waste/suggestions/${overviewDaysQuery(
      days,
      teamUuid
    )}`,
    {}
  );
  if (!r.ok)
    throw new Error(
      `agentInsights/overview/token-waste/suggestions failed: ${r.status}`
    );
  return r.json() as Promise<TokenWasteSuggestionsResponse>;
};

/** Legacy {org, developers, coaching} maturity shape, reconstructed from the
 *  bundle's raw `maturity` section. Same remap `fetchAgentInsightsMaturity`
 *  used to apply over the network. Returns undefined when the section failed
 *  server-side, so the tile falls back to its no-data state. */
export const composeMaturityFromBundle = (
  m: OverviewMaturityRaw | null
): AgentMaturityResponse | undefined =>
  m
    ? {
        success: true,
        org: m.orgMaturity,
        developers: m.byMember,
        coaching: m.coaching,
      }
    : undefined;

/** Legacy Team Health tile composite (health + efficiency + spend +
 *  driving-style merged), reconstructed from the bundle's sections. Same
 *  merge `fetchAgentInsightsTeamHealth` used to do over 4 network calls. */
export const composeTeamHealthFromBundle = (
  sections: Pick<
    AgentInsightsOverviewBundleSections,
    'health' | 'efficiency' | 'spend' | 'drivingStyle'
  >
): AgentInsightsTeamHealthResponse | undefined => {
  const { health, efficiency: eff, spend, drivingStyle: ds } = sections;
  // All four sections feed one composite of stat tiles. If any failed
  // server-side the composite would be a mix of real and zeroed numbers, which
  // reads as real data. Fall back to the tiles' no-data state instead.
  if (!health || !eff || !spend || !ds) return undefined;
  return {
    success: true,
    window: eff.window ?? spend.window,
    orgMaturity: health.orgMaturity
      ? { score: health.orgMaturity.score, band: health.orgMaturity.band }
      : null,
    avgEfficiency: Math.round(num(health.avgEfficiency)),
    tokens: health.tokens ?? '0',
    costPerDev: health.costPerDev ?? '$0',
    retryRate: typeof health.retryRate === 'number' ? health.retryRate : null,
    membersActive: num(health.membersActive),
    membersTotal: num(health.membersTotal),
    trend: (eff.trend ?? []).map((p) => ({
      date: p.month,
      efficiency: Math.round(num(p.efficiency)),
    })),
    byProject: (spend.byProject ?? []).map((p) => ({
      name: p.label,
      sessions: p.sessions,
      tokens: p.tokens,
      spend: p.spend,
      spendRaw: p.spendRaw,
    })),
    // `readFirstRate` and `fullRewrites` are nullable at the source: null means
    // "not instrumented in this window", so they stay null rather than being
    // coerced to 0 and rendered as an alarming "0%".
    drivingStyle: [
      {
        key: 'toolCallsPerTurn',
        label: 'tool calls / turn',
        value: num(ds.toolCallsPerTurn).toFixed(1),
        raw: num(ds.toolCallsPerTurn),
        danger: false,
      },
      {
        key: 'readFirstRate',
        label: 'read-first rate',
        value: pctOrDash(ds.readFirstRate, 0),
        raw: ds.readFirstRate ?? null,
        danger: false,
      },
      {
        key: 'readsPerEdit',
        label: 'reads per action',
        value: num(ds.readsPerEdit).toFixed(1),
        raw: num(ds.readsPerEdit),
        danger: false,
      },
      {
        key: 'researchPerAction',
        label: 'research per action',
        value: num(ds.researchPerAction).toFixed(1),
        raw: num(ds.researchPerAction),
        danger: false,
      },
      {
        key: 'fullRewrites',
        label: 'full rewrites',
        value: pctOrDash(ds.fullRewrites, 0),
        raw: ds.fullRewrites ?? null,
        danger: false,
      },
      {
        key: 'retryRate',
        label: 'retry rate',
        value: pctOrDash(ds.retryRate, 1),
        raw: ds.retryRate ?? null,
        danger: ds.retryRate != null && ds.retryRate >= 10,
      },
    ],
  };
};

// --- Model Quality Comparison ------------------------------------------------
// Review comments grouped by AUTHORING model (PR<->model attribution) — which
// model wrote the PR's code, not which model reviewed it. Backed by
// `pr_reviewer.PRModelAttribution` + `Comment`, joined server-side.

export interface ModelQualitySeverityCounts {
  critical: number;
  major: number;
  nit: number;
}

export interface ModelQualityRow {
  totalComments: number;
  bySeverity: ModelQualitySeverityCounts;
  /** {category_slug: count} — the 11 fixed comment_taxonomy slugs. */
  byCategory: Record<string, number>;
  /** Comments with no severity: counted in `totalComments` but in no severity
   * bucket. Absent on older backends. */
  unclassifiedComments?: number;
  /** Comments with no category: counted in `totalComments` but in no category
   * bucket. Kept separate because severity and category can fail independently.
   * Absent on older backends. */
  uncategorizedComments?: number;
  /** Fraction [0,1] of this model's comments that are critical. */
  criticalRate: number;
  /** Fraction [0,1] of this model's comments in the "security" category. */
  securityRate: number;
  /** Fraction [0,1] of comments marked addressed or bot-auto-resolved. */
  addressedRate: number;
  /** Avg PRReviewStatsTable.confidence_score (1-5) across this model's PRs, or null if none scored. */
  avgConfidence: number | null;
  /** PRs where this model was the dominant (most-turns) author. */
  prCountDominant: number;
  /** PRs where this model appears at all, incl. as a secondary/escalation model. */
  prCountAny: number;
  /** {secondary_model: pr_count} — only populated on the DOMINANT model's row;
   * how often each secondary model escalates alongside it. */
  coModels: Record<string, number>;
  /** Session spend allocated to this PR's matched commits, in USD. */
  attributedSpendUsd: number;
  pricedPrCount?: number;
  excludedPricingPrCount?: number;
  costScope?: string;
  routerPrCount: number;
  directPrCount: number;
  routerSpendUsd: number;
  directSpendUsd: number;
}

export interface ModelQualityCohort {
  prCount: number;
  /** PRs in this cohort that actually merged — the spend-per-merged-PR base. */
  mergedPrCount: number;
  commentsPerPr: number;
  /** Median (not mean) hours from PR open to merge; null when none merged. */
  medianHoursToMerge: number | null;
  attributedSpendUsd: number;
  /** Spend over the merged PRs only — the numerator matching `mergedPrCount`,
   * so spend-per-merged-PR compares cohorts honestly when merge rates differ.
   * Absent on older backends (fall back to `attributedSpendUsd`). */
  mergedAttributedSpendUsd?: number;
  totalComments: number;
  bySeverity: ModelQualitySeverityCounts;
  byCategory: Record<string, number>;
  unclassifiedComments?: number;
  uncategorizedComments?: number;
  /** Fraction [0,1] of this cohort's COMMENTS marked critical. Moves with nit
   *  volume, so it is a driver rather than a score — prefer
   *  `criticalIncidence` for "did critical work ship into review". */
  criticalRate: number;
  /** PRs in this cohort carrying at least one critical review comment. */
  criticalPrCount?: number;
  /** Fraction [0,1] of this cohort's PRs with at least one critical comment.
   *  Same denominator as CoverageAndQualityTable. Absent on older backends. */
  criticalIncidence?: number;
  securityRate: number;
  addressedRate: number;
}

/** Session-execution + spend telemetry for one router cohort. Rates are
 *  percentages, and any rate with no denominator arrives as null. */
export interface RouterCohortStats {
  sessions: number;
  turns: number;
  tokens: number;
  spendUsd: number;
  spendPerSession: number | null;
  /** Sessions excluded from spendPerSession because their model is unpriced. */
  unpricedSessions?: number;
  tokensPerSession: number | null;
  cacheHitRate: number | null;
  retryRate: number | null;
  toolFailureRate: number | null;
  interruptsPerSession: number | null;
  /** Spend on tokens that didn't land (compaction, failed retries, interrupts). */
  wastedSpendUsd: number;
  wastePct: number | null;
  /** Sessions finishing with no interrupt and no failed-tool retry. */
  cleanFinishRate: number | null;
  /** Distinct models the cohort ran on. */
  models: number;
  /** {provider: sessions} — "entelligence", "fireworks", ... */
  providers: Record<string, number>;
}

/**
 * Same-turn Router impact for the Model Quality headline.
 *
 * Both arms describe the SAME routed ledger turns: baselineUsd is Opus on the
 * original tokens, actualUsd is the customer-paid Router cost (provider cost +
 * our fee). savedUsd can be negative when Router spent more than baseline.
 * Session count is sample-size context only — never a cost denominator.
 */
export interface RouterImpact {
  baselineUsd: number;
  actualUsd: number;
  savedUsd: number;
  /** Percent of baseline; null when baseline is zero. */
  savedPct: number | null;
  turns: number;
  sessions: number;
  /** Same-turn baseline cost / turns; null when no turns. */
  costPerTurnBaseline: number | null;
  /** Same-turn customer-paid Router cost / turns; null when no turns. */
  costPerTurnActual: number | null;
  attributedPrs: number;
  mergedPrs: number;
  /** True once sessions >= minSessions (enough for a cost headline). */
  ready: boolean;
  /** True once mergedPrs >= minPrs (enough for PR outcome conclusions). */
  prReady: boolean;
  minSessions: number;
  minPrs: number;
}

export interface ModelQualityPrDetail {
  attributionStatus?: {
    linkageStatus: 'supported' | 'provisional' | 'legacy' | 'unknown';
    usageCoverage: 'partial' | 'reported_complete' | 'invalid' | 'unknown';
    pricingStatus: 'captured_estimate' | 'legacy_estimate' | 'needs_reconciliation' | 'unpriced' | 'unavailable';
    reportedModels: string[];
    costScope: string;
  };
  pr: string;
  repoName: string;
  pullNumber: number;
  /** Canonical PR link from the review record. Provider-agnostic, so a GitLab
   * or self-hosted org gets a working link; null on older rows. */
  prUrl: string | null;
  models: { model: string; turns: number; tokens: number }[];
  commentCounts: {
    total: number;
    critical: number;
    major: number;
    nit: number;
  };
  confidence: number | null;
  /** PR title from the latest review (PRReviewStatsTable.title), or null. */
  title: string | null;
  /** One-line trimmed PR summary/walkthrough blurb, or null. */
  summary: string | null;
}

/** Session-execution telemetry for one authoring model — how cleanly it drove
 *  its coding sessions (agent_insights.AgentInsightsSession grouped by model).
 *  A direct quality signal, complementing the review-comment outcomes. */
export interface MetricCoverage {
  /** Sessions that reported the field (non-NULL column). */
  measuredSessions: number;
  /** Sessions the rate's population draws from. */
  populationSessions: number;
}

export interface ModelSessionStats {
  sessions: number;
  turns: number;
  /** error->retry-same-tool sequences / tool calls, as a percent. Null when no
   * session reported a tool-call denominator: "never measured", not "zero". */
  retryRate: number | null;
  /** % of tool calls that failed; null when unmeasured. */
  toolFailureRate: number | null;
  /** Null when unmeasured. */
  interruptsPerSession: number | null;
  /** The basis for `retryRate` / `interruptsPerSession`. Both are computed over
   * the sessions that actually REPORTED the field, because a client that never
   * sent it must not sit in the denominator either. When `measuredSessions` is
   * below `populationSessions` the rate describes a subset, and the UI has to
   * say so — a rate over 28 of 9,400 sessions served bare reads as fleet-wide.
   * (backend#4585, which replaced a >=50% suppression gate with disclosure.) */
  retryCoverage?: MetricCoverage;
  interruptCoverage?: MetricCoverage;
  /** The basis for `toolFailureRate`, which needs one more field than the other
   * two: the rate is tool-call-WEIGHTED, so `weightedCalls` is the volume it is
   * averaged over. Two rows on one page can differ by an order of magnitude
   * there (prod: 35.3% over 907 calls next to 3.5% over 16,241), and without it
   * a rate from a handful of single-turn sessions ranks against a rate from
   * thousands of calls as though they measured the same thing. */
  toolFailureCoverage?: MetricCoverage & { weightedCalls: number };
  /** Avg efficiency score (0-100); null when unscored. */
  efficiency: number | null;
  /** Avg context-pressure as a percent (0-100); null when unmeasured. */
  contextPressure: number | null;
  /** Wasted tokens as a share of the model's tokens (percent); null when the
   * model reports no tokens. `wasteTokens` carries the per-cause absolute split. */
  wastePct: number | null;
  /** Tokens spent on work that didn't land, by cause. */
  wasteTokens: {
    compaction: number;
    failedRetry: number;
    repeatedEdit: number;
    interrupted: number;
    total: number;
  };
}

export interface ModelQualityComparisonResponse {
  success: boolean;
  models: Record<string, ModelQualityRow>;
  /** Conservation receipt for the spend column. Model Quality is a subset of
   * the same task-clock ledger shown on Agent Insights, so attributed spend can
   * never exceed `agentInsightsSpendUsd`. Optional during backend rollout. */
  spendReconciliation?: {
    source: 'agentInsightsTaskClock';
    agentInsightsSpendUsd: number;
    attributedSpendUsd: number;
    /** Agent spend in the window that did not reach these reviewed PRs. */
    unattributedSpendUsd: number;
    /** PR candidate total, including zero-cost repair, before conservation. */
    preReconciliationSpendUsd: number;
    adjustedModelCount: number;
  } | null;
  /** Attribution coverage over ONE population: every reviewed PR whose PR was
   * created in the window, split by whether it resolved to an authoring model.
   * Computed server-side, so the gauge never divides two different windows. */
  coverage: {
    reviewedPrCount: number;
    attributedPrCount: number;
    unattributedPrCount: number;
    /** Reviewed PRs whose author has at least one synced coding-agent session —
     * the only fair denominator for a coverage percentage, since the other two
     * buckets can never resolve however well the pipeline works. Optional: an
     * older backend omits it and callers fall back to `reviewedPrCount`. */
    attributablePrCount?: number;
    /** Reviewed PRs opened by a machine account. No agent, so never resolvable. */
    automationPrCount?: number;
    /** Reviewed PRs whose author login has no linked synced-session identity.
     * This does not prove that the CLI was never installed. */
    noAgentSessionPrCount?: number;
    /** Unresolved PRs retained as eligible because author/session identity is uncertain. */
    uncertainAttributabilityPrCount?: number;
    /** Reviewed rows with no provider creation date, windowed by last update instead. */
    createdAtFallbackPrCount?: number;
  };
  unattributed: { prCount: number; commentCount: number };
  /** {"owner/repo#123": [[model, turns, tokens], ...]} for the per-PR drill-down. */
  prRoster: Record<string, [string, number, number][]>;
  /** {dominant_model: [pr_detail, ...]} — a model's PRs for the drill-down. */
  prsByDominant: Record<string, ModelQualityPrDetail[]>;
  /** Mutually exclusive groups. ``unknown`` has exact model/PR attribution but
   * lacks router telemetry, so it must not be presented as direct traffic. */
  cohorts: {
    router: ModelQualityCohort;
    direct: ModelQualityCohort;
    unknown: ModelQualityCohort;
  };
  /** The same router/direct split measured on the SESSION side (spend, tokens,
   * cache, retries). Optional: absent when the backend predates it. */
  routerCohorts?: {
    router: RouterCohortStats;
    direct: RouterCohortStats;
    unknown: RouterCohortStats;
  };
  /**
   * Paired same-turn Router impact: ledger baseline vs actual provider cost on
   * the same routed turns, plus delivered Router PR counts. Optional: absent on
   * backends that predate the field.
   */
  routerImpact?: RouterImpact;
  /** Session-execution telemetry keyed by authoring model. Org-wide within the
   * window (not narrowed by any repo filter). Older backends injected a scalar
   * `pendingSessions` count into this map; readers must treat non-object
   * values as metadata, not model rows. */
  sessionStats: Record<string, ModelSessionStats>;
  /** Sessions in the window with no token usage synced yet (Cursor / not yet
   * analyzed) — excluded from `sessionStats`. Absent on older backends, which
   * injected this count into `sessionStats` instead. */
  sessionStatsPending?: number;
  dateRange: {
    start: string;
    end: string;
    /** Day-anchored bound the cohort/PR/session aggregates used — the current
     * partial day is excluded so counts move once per day, not per reload.
     * Equals `end` for historical windows; absent on older backends. */
    cohortEnd?: string;
  };
  error?: string;
  code?: string;
}

export interface ModelQualityComparisonParams {
  start?: string;
  end?: string;
}

export const fetchModelQualityComparison = async (
  params: ModelQualityComparisonParams = {}
): Promise<ModelQualityComparisonResponse> => {
  const qs = new URLSearchParams();
  if (params.start) qs.set('start', params.start);
  if (params.end) qs.set('end', params.end);
  const query = qs.toString();
  const r = await getWithAuth(
    `${apiUrl}modelQualityComparison/${query ? `?${query}` : ''}`
  );
  if (!r.ok) await throwModelRouterError(r, 'modelQualityComparison');
  return r.json();
};

// --- Session Explorer (ENT-4777 / SE-10) ------------------------------------
// Contract: knowledge-base 10-session-explorer.md, "FE endpoints". JWT GETs,
// org admin or the member themselves. The map document is then fetched from a
// presigned S3 URL with a plain fetch (no auth header), validated by Zod.

const sessionExplorerEndpoint = 'agentInsights/member/session-explorer/';

/** Error carrying the backend `code` (e.g. `not_found`) so the UI can pick
 * human copy without ever showing a status code or URL. */
export class SessionExplorerError extends Error {
  code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = 'SessionExplorerError';
    this.code = code;
  }
}

async function sessionExplorerFailure(
  r: Response,
  what: string
): Promise<SessionExplorerError> {
  let code = r.status === 404 ? 'not_found' : 'internal_error';
  try {
    const body: unknown = await r.json();
    if (
      body &&
      typeof body === 'object' &&
      'code' in body &&
      typeof body.code === 'string'
    ) {
      code = body.code;
    }
  } catch {
    // non-JSON error body: keep the status-derived code
  }
  return new SessionExplorerError(`${what} failed: ${r.status}`, code);
}

export const fetchSessionExplorerStatus = async (
  userUuid: string
): Promise<SessionExplorerStatus> => {
  const qs = new URLSearchParams({ user_uuid: userUuid });
  const r = await getWithAuth(
    `${apiUrl}${sessionExplorerEndpoint}status/?${qs.toString()}`
  );
  if (!r.ok) throw await sessionExplorerFailure(r, 'sessionExplorer.status');
  return sessionExplorerStatusSchema.parse(await r.json());
};

export const fetchSessionExplorerSessions = async (params: {
  userUuid: string;
  from?: string;
  to?: string;
  search?: string;
  pageSize?: number;
  cursor?: string | null;
}): Promise<SessionExplorerSessionsPage> => {
  const qs = new URLSearchParams({ user_uuid: params.userUuid });
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.search) qs.set('search', params.search);
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.cursor) qs.set('cursor', params.cursor);
  const r = await getWithAuth(
    `${apiUrl}${sessionExplorerEndpoint}sessions/?${qs.toString()}`
  );
  if (!r.ok) throw await sessionExplorerFailure(r, 'sessionExplorer.sessions');
  return sessionExplorerSessionsSchema.parse(await r.json());
};

/** Max ids per `map/` call (contract: 1-20). */
export const SESSION_MAP_BATCH_MAX = 20;

export const fetchSessionExplorerMapUrls = async (
  userUuid: string,
  sessionIds: string[]
): Promise<SessionExplorerMapBatch> => {
  const qs = new URLSearchParams({ user_uuid: userUuid });
  for (const id of sessionIds.slice(0, SESSION_MAP_BATCH_MAX)) {
    qs.append('session_id', id);
  }
  const r = await getWithAuth(
    `${apiUrl}${sessionExplorerEndpoint}map/?${qs.toString()}`
  );
  if (!r.ok) throw await sessionExplorerFailure(r, 'sessionExplorer.map');
  return sessionExplorerMapBatchSchema.parse(await r.json());
};

/** The agent-optimization harness ledger for one session (backend #5925).
 * Same auth and access rule as the other session-explorer GETs. It is a
 * sidecar: a 400/403/503 (or a body that does not parse) reads as
 * "unavailable", so the explorer renders nothing harness-related instead of
 * an error. */
export const fetchSessionExplorerHarness = async (
  userUuid: string,
  sessionId: string
): Promise<HarnessLedger> => {
  const qs = new URLSearchParams({
    user_uuid: userUuid,
    session_id: sessionId,
  });
  const r = await getWithAuth(
    `${apiUrl}${sessionExplorerEndpoint}harness/?${qs.toString()}`
  );
  if (!r.ok) return HARNESS_UNAVAILABLE;
  let body: unknown;
  try {
    body = await r.json();
  } catch {
    return HARNESS_UNAVAILABLE;
  }
  const parsed = harnessLedgerSchema.safeParse(body);
  return parsed.success ? parsed.data : HARNESS_UNAVAILABLE;
};

/** GET the presigned map URL. Plain `fetch` on purpose: the URL carries its
 * own signature, and an Authorization header would break the S3 signature.
 * S3 serves `Content-Encoding: gzip`, so the browser inflates it for us. */
export const fetchSessionMapDocument = async (
  url: string
): Promise<SessionMap> => {
  const r = await fetch(url, { credentials: 'omit' });
  if (!r.ok) {
    throw new SessionExplorerError(
      `sessionMap.document failed: ${r.status}`,
      r.status === 403 ? 'expired' : 'internal_error'
    );
  }
  const parsed = sessionMapSchema.safeParse(await r.json());
  if (!parsed.success) {
    throw new SessionExplorerError(
      'sessionMap.document failed validation',
      'unsupported_schema'
    );
  }
  return parsed.data;
};
